import { ENVIRONMENT } from '@/config';

export declare interface NetworkPartnership {
  readonly text: string;
  readonly logoUrl: string;
}

export declare interface Whitelabel {
  readonly name: () => string;
  readonly code: string;
  readonly readable: string;
  readonly logo: string;
  readonly logoType: string;
  readonly colourPrimary: string;
  readonly colourSecondary: string;
  readonly colourLight: string;
  readonly colourLighter: string;
  readonly buttonColour: string;
  readonly buttonBgColour: string;
  readonly colourTitle: string;
  readonly bgColourPrimary: string;
  readonly bgColourSecondary: string;
  readonly fontPrimary: string;
  readonly fontSecondary: string;
  readonly fontURL: string;
  readonly heroBg: string;
  readonly loginBg: string;
  readonly favicon: string;
  readonly liveSubDomain: string;
  readonly sandboxSubDomain: string;
}

export const hexagoneIds: readonly number[] = [1050, 1096];
export const finsburyIds: readonly number[] = [881];

function whiteName(this: Whitelabel): string {
  if (ENVIRONMENT === 'production') {
    return this.liveSubDomain;
  }
  return this.code;
}

const FLB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'flb',
  readable: 'fallback',
  logo: '/static/logo-placeholder.png',
  logoType: '/static/logo-placeholder-round.png',
  colourPrimary: '#888888',
  colourSecondary: '#888888',
  colourLight: '#888888dd',
  colourLighter: '#88888815',
  buttonColour: '#888888',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#888888',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '#AAAAAA',
  favicon: '',
  liveSubDomain: 'flb',
  sandboxSubDomain: 'flb',
};

const CP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cpl',
  readable: 'Capital Platforms',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'capitalplatforms',
  sandboxSubDomain: 'cpl',
};

const SAY: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'say',
  readable: 'Simon Says LLC FZ',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'say',
  sandboxSubDomain: 'say',
};
const SVN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'svn',
  readable: 'SVN Capital',
  logo: '/static/whitelabels/svn/svn-logo.png',
  logoType: '/static/whitelabels/svn/svn-logo.png',
  colourPrimary: '#212529',
  colourSecondary: '#B78D7A',
  colourLight: '#212529',
  colourLighter: '#212529',
  buttonColour: '#212529',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#212529',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Arial',
  fontSecondary: 'Nunito Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/svn/svn-logo-bg.png")',
  favicon: '/static/whitelabels/svn/svn-logo-fav.png',
  liveSubDomain: 'svn',
  sandboxSubDomain: 'svn',
};

const SVA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sva',
  readable: 'SVN Capital Asia Limited',
  logo: '/static/whitelabels/svn/svn-logo.png',
  logoType: '/static/whitelabels/svn/svn-logo.png',
  colourPrimary: '#212529',
  colourSecondary: '#B78D7A',
  colourLight: '#212529',
  colourLighter: '#212529',
  buttonColour: '#212529',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#212529',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Arial',
  fontSecondary: 'Nunito Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/svn/svn-logo-bg.png")',
  favicon:
    '/static/whitelabels/svn/svn-logo-fav.png',
  liveSubDomain: 'sva',
  sandboxSubDomain: 'sva',
};

const AMA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ama',
  readable: 'Austen Morris',
  logo: '/static/ama-logo.png',
  logoType: '/static/ama-logo.png',
  colourPrimary: '#002550',
  colourSecondary: '#F15C22',
  colourLight: '#002550dd',
  colourLighter: '#00255015',
  buttonColour: '#002550',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#002550',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Arial',
  fontSecondary: 'Nunito Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/ama-login-bg.jpeg")',
  favicon:
    'https://austenmorris.com/wp-content/uploads/2020/06/cropped-AMA-Favicon-32x32.jpg',
  liveSubDomain: 'austenmorris',
  sandboxSubDomain: 'ama',
};

const AME: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ame',
  readable: 'Austen Morris Europe',
  logo: '/static/ame-logo.png',
  logoType: '/static/ame-logo.png',
  colourPrimary: '#002550',
  colourSecondary: '#F15C22',
  colourLight: '#002550dd',
  colourLighter: '#00255015',
  buttonColour: '#002550',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#002550',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Arial',
  fontSecondary: 'Nunito Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/ama-login-bg.jpeg")',
  favicon:
    'https://austenmorris.com/wp-content/uploads/2020/06/cropped-AMA-Favicon-32x32.jpg',
  liveSubDomain: 'ame',
  sandboxSubDomain: 'ame',
};

const AMI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ain',
  readable: 'Austen Morris International',
  logo: '/static/amai-logo.png',
  logoType: '/static/amai-logo.png',
  colourPrimary: '#002550',
  colourSecondary: '#F15C22',
  colourLight: '#002550dd',
  colourLighter: '#00255015',
  buttonColour: '#002550',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#002550',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Arial',
  fontSecondary: 'Nunito Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/ama-login-bg.jpeg")',
  favicon:
    'https://austenmorris.com/wp-content/uploads/2020/06/cropped-AMA-Favicon-32x32.jpg',
  liveSubDomain: 'austenmorrisinternational',
  sandboxSubDomain: 'ain',
};

const FOA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'foa',
  readable: 'Fortis Arbor Wealth',
  logo: '/static/whitelabels/foa/foa-logo.png',
  logoType: '/static/whitelabels/foa/foa-logo.png',
  colourPrimary: '#002550',
  colourSecondary: '#F15C22',
  colourLight: '#002550dd',
  colourLighter: '#00255015',
  buttonColour: '#002550',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#002550',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Arial',
  fontSecondary: 'Nunito Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/foa/foa-background.png")',
  favicon: '/static/whitelabels/foa/fav-foa-logo.png',
  liveSubDomain: 'fortisarborwealth',
  sandboxSubDomain: 'foa',
};
const HEX: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hex',
  readable: 'Hexagone Capital',
  logo: '/static/hex-logo-new.png',
  logoType: '/static/hex-logo-new.png',
  colourPrimary: '#324158',
  colourSecondary: '#B21E44',
  colourLight: '#B21E44dd',
  colourLighter: '#B21E4415',
  buttonColour: '#324158',
  buttonBgColour: '#EFEFEF',
  colourTitle: '#324158',
  bgColourPrimary: '#EAECEE',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: 'Libre Caslon Text',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Libre+Caslon+Text:ital,wght@0,400;0,700;1,400&family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/hexagone-login-bg.jpeg")',
  favicon:
    'https://static.wixstatic.com/media/bab916_22b9f0c3998e4b4cbb1a0d0c29a2bc10~mv2.png/v1/fill/w_32%2Ch_32%2Clg_1%2Cusm_0.66_1.00_0.01/bab916_22b9f0c3998e4b4cbb1a0d0c29a2bc10~mv2.png',
  liveSubDomain: 'hexagone',
  sandboxSubDomain: 'hex',
};

const HUK: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'huk',
  readable: 'Hoxtonwealth',
  logo: '/static/whitelabels/huk/huk-logo.png',
  logoType: '/static/whitelabels/huk/huk-logo.png',
  colourPrimary: '#61c9c0',
  colourSecondary: '#77b5b0',
  colourLight: '#c6edea',
  colourLighter: '#c6edea',
  buttonColour: '#61c9c0',
  buttonBgColour: '#EFEFEF',
  colourTitle: '#61c9c0',
  bgColourPrimary: '#EAECEE',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: 'Libre Caslon Text',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Libre+Caslon+Text:ital,wght@0,400;0,700;1,400&family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url(/static/whitelabels/huk/bg-logo.png)',
  favicon: '/static/whitelabels/huk/fav-icon.png',
  liveSubDomain: 'huk',
  sandboxSubDomain: 'huk',
};

const SFP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sfp',
  readable: 'Stein',
  logo: '/static/sfp-logo.png',
  logoType: '/static/sfp-logomark.png',
  colourPrimary: '#2A3439',
  colourSecondary: '#BD935E',
  colourLight: '#2A3439dd',
  colourLighter: '#2A343915',
  buttonColour: '#2A3439',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#2A3439',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto Condensed',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Roboto+Condensed:ital,wght@0,300;0,400;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/stein-login-bg.jpeg")',
  favicon: '/static/whitelabels/sfp/stein-favicon.png',
  liveSubDomain: 'stein',
  sandboxSubDomain: 'sfp',
};
const JIB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'jfp',
  readable: 'Jiib',
  logo: '/static/jiib-logo.jpeg',
  logoType: '/static/jiib-logo.jpeg',
  colourPrimary: '#636363',
  colourSecondary: '#EE1C25',
  colourLight: '#B00000dd',
  colourLighter: '#B0000015',
  buttonColour: '#636363',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#B00000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("https://www.jiibfinancial.com/images/Retirement_Small.jpg")',
  favicon: 'https://www.jiibfinancial.com/templates/zt_maju25/u25-favicon.png',
  liveSubDomain: 'jiib',
  sandboxSubDomain: 'jfp',
};

const BGA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bga',
  readable: 'Beacon Global Advisory Network',
  logo: '/static/bgan-logotype-white-horizontal.jpg',
  logoType: '/static/bgan-logotype-white-vertical.jpg',
  colourPrimary: '#5a3d3a',
  colourSecondary: '#5a3d3a',
  colourLight: '#5a3d3add',
  colourLighter: '#5a3d3a15',
  buttonColour: '#5a3d3a',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#5a3d3a',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '#5a3d3a',
  favicon: 'https://bganetwork.com/wp-content/uploads/2021/01//01-favicon.png',
  liveSubDomain: 'bga',
  sandboxSubDomain: 'bga',
};

const FIN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fa',
  readable: 'Finsbury',
  logo: '/static/finsbury-logo.png',
  logoType: '/static/finsbury-logo.png',
  colourPrimary: '#222222',
  colourSecondary: '#a49564',
  colourLight: '#a49564dd',
  colourLighter: '#a4956415',
  buttonColour: '#222222',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#222222',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: 'Open Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/finsbury-login-bg.jpeg")',
  favicon: '',
  liveSubDomain: 'finsbury',
  sandboxSubDomain: 'fa',
};

const CRK: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'crk',
  readable: 'Carrick',
  logo: '/static/carrick-logo-long.png',
  logoType: '/static/carrick-logo-square.png',
  colourPrimary: '#112031',
  colourSecondary: '#AC677A',
  colourLight: '#112031dd',
  colourLighter: '#11203115',
  buttonColour: '#112031',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#112031',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Merriweather',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: '#112031',
  favicon: '/static/carrick-favicon.png',
  liveSubDomain: 'carrick',
  sandboxSubDomain: 'crk',
};

const GLO: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'glo',
  readable: 'Globaleye',
  logo: '/static/globaleye-logo.png',
  logoType: '/static/globaleye-logo.png',
  colourPrimary: '#07424d',
  colourSecondary: '#B48D5E',
  colourLight: '#07424ddd',
  colourLighter: '#07424d15',
  buttonColour: '#07424d',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#07424d',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Inter',
  fontSecondary: 'Lora',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Inter:wght@100;400;600&family=Lora:wght@400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/globaleye-login-bg.jpeg")',
  favicon: '/static/globaleye-logo.png',
  liveSubDomain: 'globaleye',
  sandboxSubDomain: 'glo',
};

const NEB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'neb',
  readable: 'NEBA Wealth Management',
  logo: '/static/whitelabels/neb/neb-logo.png',
  logoType: '/static/whitelabels/neb/neb-logo.png',
  colourPrimary: '#383838',
  colourSecondary: '#009982',
  colourLight: '#009982dd',
  colourLighter: '#00998215',
  buttonColour: '#383838',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#383838',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Montserrat',
  fontSecondary: 'Montserrat',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Montserrat:wght@100;200;300;400;500;600;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/neb/neb-background.jpg")',
  favicon: '/static/whitelabels/neb/neb-favicon.png',
  liveSubDomain: 'neb',
  sandboxSubDomain: 'neb',
};

const MML: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mml',
  readable: 'M1 Malaysia Limited',
  logo: '/static/whitelabels/mml/mml-logo.png',
  logoType: '/static/whitelabels/mml/mml-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#3e64af',
  colourLight: '#3e64afdd',
  colourLighter: '#3e64af15',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Nunito',
  fontSecondary: 'Nunito',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Nunito:wght@200;300;400;500;600;700;800&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/mml/mml-background.jpg")',
  favicon: '/static/whitelabels/mml/mml-favicon.png',
  liveSubDomain: 'm1malaysia',
  sandboxSubDomain: 'mml',
};

const ABF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'abf',
  readable: 'Absolute Financial Solutions',
  logo: '/static/whitelabels/abf/abf-logo.png',
  logoType: '/static/whitelabels/abf/abf-logo.png',
  colourPrimary: '#1A3866',
  colourSecondary: '#1A3866',
  colourLight: '#1A3866dd',
  colourLighter: '#1A386615',
  buttonColour: '#1A3866',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1A3866',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Hind',
  fontSecondary: 'Hind',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Hind:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/abf/abf-background.jpg")',
  favicon: '/static/whitelabels/abf/abf-favicon.png',
  liveSubDomain: 'absolute',
  sandboxSubDomain: 'abf',
};

const CBA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cba',
  readable: 'Brite Advisors',
  logo: '/static/whitelabels/cba/cba-logo.png',
  logoType: '/static/whitelabels/cba/cba-logo.png',
  colourPrimary: '#000',
  colourSecondary: '#b00e23',
  colourLight: '#b00e23dd',
  colourLighter: '#b00e2315',
  buttonColour: '#000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Poppins',
  fontSecondary: 'Mulish',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Mulish:wght@300;400;600&family=Poppins:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/cba/cba-background.jpeg")',
  favicon: '/static/whitelabels/cba/cba-favicon.png',
  liveSubDomain: 'cba',
  sandboxSubDomain: 'cba',
};

const HAL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hal',
  readable: 'Holborn Assets',
  logo: '/static/whitelabels/hal/hal-logo.png',
  logoType: '/static/whitelabels/hal/hal-logo.png',
  colourPrimary: '#023660',
  colourSecondary: '#007fb8',
  colourLight: '#007fb8dd',
  colourLighter: '#007fb815',
  buttonColour: '#023660',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#023660',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: 'Open Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/hal/hal-background.jpg")',
  favicon: '/static/whitelabels/hal/hal-favicon.png',
  liveSubDomain: 'holborn',
  sandboxSubDomain: 'hal',
};

const KMI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'kmi',
  readable: 'KMI Consultants',
  logo: '/static/whitelabels/kmi/kmi-logo.gif',
  logoType: '/static/whitelabels/kmi/kmi-logo.gif',
  colourPrimary: '#183562',
  colourSecondary: '#ae9666',
  colourLight: '#ae9666dd',
  colourLighter: '#ae966615',
  buttonColour: '#183562',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#183562',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/kmi/kmi-background.jpg")',
  favicon: '/static/whitelabels/kmi/kmi-favicon.png',
  liveSubDomain: 'kmi',
  sandboxSubDomain: 'kmi',
};

const CHB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'chb',
  readable: 'Chase Buchanan',
  logo: '/static/whitelabels/chb/chb-logo.png',
  logoType: '/static/whitelabels/chb/chb-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#418FDE',
  colourLight: '#418FDEdd',
  colourLighter: '#418FDE15',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Convergence',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Convergence&family=Playfair+Display:wght@400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/chb/chb-background.jpg")',
  favicon: '/static/whitelabels/chb/chb-favicon.png',
  liveSubDomain: 'chb',
  sandboxSubDomain: 'chb',
};

const PTM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ptm',
  readable: 'Portman Financial Management',
  logo: '/static/whitelabels/ptm/ptm-logo.png',
  logoType: '/static/whitelabels/ptm/ptm-logo.png',
  colourPrimary: '#242d61',
  colourSecondary: '#4d5d86',
  colourLight: '#4d5d86dd',
  colourLighter: '#4d5d8615',
  buttonColour: '#242d61',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#242d61',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Montserrat',
  fontSecondary: 'Domine',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Domine:wght@400;600&family=Montserrat:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/ptm/ptm-background.jpeg")',
  favicon: '/static/whitelabels/ptm/ptm-favicon.png',
  liveSubDomain: 'ptm',
  sandboxSubDomain: 'ptm',
};

const FAW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'faw',
  readable: 'United Advisers Group',
  logo: '/static/whitelabels/faw/faw-logo.png',
  logoType: '/static/whitelabels/faw/faw-logo.png',
  colourPrimary: '#0d1e64',
  colourSecondary: '#14A2CB',
  colourLight: '#14A2CBdd',
  colourLighter: '#14A2CB15',
  buttonColour: '#0d1e64',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#0d1e64',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/faw/faw-background.jpg")',
  favicon: '/static/whitelabels/faw/faw-favicon.png',
  liveSubDomain: 'unitedadvisers',
  sandboxSubDomain: 'faw',
};

const FSP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fsp',
  readable: 'FS Platinum',
  logo: '/static/whitelabels/fsp/fsp-logo.png',
  logoType: '/static/whitelabels/fsp/fsp-logo.png',
  colourPrimary: '#333;',
  colourSecondary: '#3f3f3f',
  colourLight: '#3f3f3fdd',
  colourLighter: '#3f3f3f15',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/fsp/fsp-background.jpg")',
  favicon: '/static/whitelabels/fsp/fsp-favicon.png',
  liveSubDomain: 'fsplatinum',
  sandboxSubDomain: 'fsp',
};

const LSM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'lsm',
  readable: 'Lifestyle Management Ltd',
  logo: '/static/whitelabels/lsm/lsm-logo.png',
  logoType: '/static/whitelabels/lsm/lsm-logo.png',
  colourPrimary: '#333;',
  colourSecondary: '#2B547E',
  colourLight: '#2B547Edd',
  colourLighter: '#2B547E15',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#2B547E',
  favicon: '/static/whitelabels/lsm/lsm-favicon.png',
  liveSubDomain: 'lsmafrica',
  sandboxSubDomain: 'lsm',
};

const NWA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'nwa',
  readable: 'New World Asset Management',
  logo: '/static/whitelabels/nwa/nwa-logo.png',
  logoType: '/static/whitelabels/nwa/nwa-logo.png',
  colourPrimary: '#333;',
  colourSecondary: '#00254b',
  colourLight: '#00254bdd',
  colourLighter: '#00254b15',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#00254b',
  favicon: '/static/whitelabels/nwa/nwa-favicon.png',
  liveSubDomain: 'nwa',
  sandboxSubDomain: 'nwa',
};

const BLU: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'blu',
  readable: 'Bluestar AMG',
  logo: '/static/whitelabels/blu/blu-logo.png',
  logoType: '/static/whitelabels/blu/blu-logo.png',
  colourPrimary: '#001e7d',
  colourSecondary: '#12b8e6',
  colourLight: '#12b8e6dd',
  colourLighter: '#12b8e615',
  buttonColour: '#001e7d',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#444',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/blu/blu-background.jpg")',
  favicon: '/static/whitelabels/blu/blu-favicon.png',
  liveSubDomain: 'blu',
  sandboxSubDomain: 'blu',
};

const CHC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'chc',
  readable: 'Chartercross Capital Management DWC LLC',
  logo: '/static/whitelabels/chc/chc-logo.png',
  logoType: '/static/whitelabels/chc/chc-logo.png',
  colourPrimary: '#1f396f',
  colourSecondary: '#E1C500',
  colourLight: '#E1C500dd',
  colourLighter: '#E1C50015',
  buttonColour: '#1f396f',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1f396f',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/chc/chc-background.jpg")',
  favicon: '/static/whitelabels/chc/chc-favicon.png',
  liveSubDomain: 'chartercross',
  sandboxSubDomain: 'chc',
};

const EDG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'edg',
  readable: 'Edge Financial',
  logo: '/static/whitelabels/edg/edg-logo.png',
  logoType: '/static/whitelabels/edg/edg-logo.png',
  colourPrimary: '#1a396a',
  colourSecondary: '#1a396a',
  colourLight: '#1a396add',
  colourLighter: '#1a396a15',
  buttonColour: '#1a396a',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#444',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/edg/edg-background.jpg")',
  favicon: '/static/whitelabels/edg/edg-favicon.png',
  liveSubDomain: 'edgefinance',
  sandboxSubDomain: 'edg',
};

const FUT: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fut',
  readable: 'Future',
  logo: '/static/whitelabels/fut/fut-logo.png',
  logoType: '/static/whitelabels/fut/fut-logo.png',
  colourPrimary: '#4e4e4e',
  colourSecondary: '#5c2e91',
  colourLight: '#5c2e91dd',
  colourLighter: '#5c2e9115',
  buttonColour: '#4e4e4e',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#4e4e4e',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Quicksand',
  fontSecondary: 'Source Sans Pro',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;600&family=Source+Sans+Pro:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/fut/fut-background.jpg")',
  favicon: '/static/whitelabels/fut/fut-favicon.png',
  liveSubDomain: 'fut',
  sandboxSubDomain: 'fut',
};

const GND: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gnd',
  readable: 'GlobalNet',
  logo: '/static/whitelabels/gnd/gnd-logo.png',
  logoType: '/static/whitelabels/gnd/gnd-logo.png',
  colourPrimary: '#444',
  colourSecondary: '#961B22',
  colourLight: '#961B22dd',
  colourLighter: '#961B2215',
  buttonColour: '#444',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#444',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#9E9D9D',
  favicon: '/static/whitelabels/gnd/gnd-favicon.png',
  liveSubDomain: 'globalnet',
  sandboxSubDomain: 'gnd',
};

const RWM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'rwm',
  readable: 'Regency Wealth Management',
  logo: '/static/whitelabels/rwm/rwm-logo.png',
  logoType: '/static/whitelabels/rwm/rwm-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#2e3182',
  colourLight: '#2e3182dd',
  colourLighter: '#2e318215',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/rwm/rwm-background.jpg")',
  favicon: '/static/whitelabels/rwm/rwm-favicon.png',
  liveSubDomain: 'regency-wealth',
  sandboxSubDomain: 'rwm',
};

const RWB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'rwb',
  readable: 'Renaissance',
  logo: '/static/whitelabels/rwb/rwb-logo.png',
  logoType: '/static/whitelabels/rwb/rwb-logo.png',
  colourPrimary: '#1a1a1a',
  colourSecondary: '#b53633',
  colourLight: '#b53633dd',
  colourLighter: '#b5363315',
  buttonColour: '#1a1a1a',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1a1a1a',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Source Sans Pro',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/rwb/rwb-background.jpg")',
  favicon: '/static/whitelabels/rwb/rwb-favicon.png',
  liveSubDomain: 'rwb-direct',
  sandboxSubDomain: 'rwb',
};

const RAP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'rap',
  readable: 'Renascence & Partners LLC',
  logo: '/static/whitelabels/rap/rap-logo.png',
  logoType: '/static/whitelabels/rap/rap-logo.png',
  colourPrimary: '#042D60',
  colourSecondary: '#EFCEAB',
  colourLight: '#EFCEABdd',
  colourLighter: '#EFCEAB15',
  buttonColour: '#042D60',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#042D60',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Poppins',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/rap/rap-background.jpg")',
  favicon: '/static/whitelabels/rap/rap-favicon.jpg',
  liveSubDomain: 'rap',
  sandboxSubDomain: 'rap',
};

const OPC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'opc',
  readable: 'Ocean Park Capital',
  logo: '/static/whitelabels/opc/opc-logo.png',
  logoType: '/static/whitelabels/opc/opc-logo.png',
  colourPrimary: '#222',
  colourSecondary: '#325A3A',
  colourLight: '#325A3Add',
  colourLighter: '#325A3A15',
  buttonColour: '#222',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#222',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#325A3A',
  favicon: '/static/whitelabels/opc/opc-favicon.png',
  liveSubDomain: 'opc',
  sandboxSubDomain: 'opc',
};

const MNP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mnp',
  readable: 'OFS Asia',
  logo: '/static/whitelabels/mnp/mnp-logo.png',
  logoType: '/static/whitelabels/mnp/mnp-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#74A5D1',
  colourLight: '#74A5D1dd',
  colourLighter: '#74A5D115',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#74A5D1',
  favicon: '/static/whitelabels/mnp/mnp-favicon.png',
  liveSubDomain: 'mnp',
  sandboxSubDomain: 'mnp',
};

const IMC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'imc',
  readable: 'Imperium Capital',
  logo: '/static/whitelabels/imc/imc-logo.png',
  logoType: '/static/whitelabels/imc/imc-logo.png',
  colourPrimary: '#903600',
  colourSecondary: '#903600',
  colourLight: '#7A7A7A',
  colourLighter: '#4C6BAC15',
  buttonColour: '#141412',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#141412',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Inter',
  fontSecondary: 'Inter',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/imc/imc-background.jpg")',
  favicon: '/static/whitelabels/imc/imc-favicon.png',
  liveSubDomain: 'imperiumcapital',
  sandboxSubDomain: 'imc',
};

const TSF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'tsf',
  readable: 'Three Sixty Financial',
  logo: '/static/whitelabels/tsf/tsf-logo.png',
  logoType: '/static/whitelabels/tsf/tsf-logo.png',
  colourPrimary: '#002E5D',
  colourSecondary: '#002E5D',
  colourLight: '#002E5Ddd',
  colourLighter: '#002E5D15',
  buttonColour: '#002E5D',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#444',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/tsf/tsf-background.jpg")',
  favicon: '/static/whitelabels/tsf/tsf-favicon.png',
  liveSubDomain: 'tsf',
  sandboxSubDomain: 'tsf',
};

const EDW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'edw',
  readable: 'Edge Wealth',
  logo: '/static/whitelabels/edw/edw-logo.png',
  logoType: '/static/whitelabels/edw/edw-logo.png',
  colourPrimary: '#333132',
  colourSecondary: '#937624',
  colourLight: '#937624dd',
  colourLighter: '#93762415',
  buttonColour: '#333132',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333132',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/edw/edw-background.jpg")',
  favicon: '/static/whitelabels/edw/edw-favicon.png',
  liveSubDomain: 'edw',
  sandboxSubDomain: 'edw',
};

const CHL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'chl',
  readable: 'Continental Holdings Ltd',
  logo: '/static/whitelabels/chl/chl-logo.png',
  logoType: '/static/whitelabels/chl/chl-logo.png',
  colourPrimary: '#000',
  colourSecondary: '#0C2F6D',
  colourLight: '#0C2F6Ddd',
  colourLighter: '#0C2F6D15',
  buttonColour: '#0C2F6D',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: 'Hind',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Hind:wght@300;400;600&family=Open+Sans:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/chl/chl-background.jpg")',
  favicon: '/static/whitelabels/chl/chl-favicon.png',
  liveSubDomain: 'chl',
  sandboxSubDomain: 'chl',
};

const ACO: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'aco',
  readable: 'Acorn Partners',
  logo: '/static/whitelabels/aco/aco-logo.png',
  logoType: '/static/whitelabels/aco/aco-logo.png',
  colourPrimary: '#444444',
  colourSecondary: '#F79221',
  colourLight: '#F79221dd',
  colourLighter: '#F7922115',
  buttonColour: '#444444',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#444444',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/aco/aco-background.jpg")',
  favicon: '/static/whitelabels/aco/aco-favicon.png',
  liveSubDomain: 'acorn',
  sandboxSubDomain: 'aco',
};

const AFG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'afg',
  readable: 'Atlas Financial Group',
  logo: '/static/whitelabels/afg/afg-logo.png',
  logoType: '/static/whitelabels/afg/afg-logo.png',
  colourPrimary: '#111820',
  colourSecondary: '#C8A058',
  colourLight: '#C8A058dd',
  colourLighter: '#C8A05815',
  buttonColour: '#111820',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#111820',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Source Sans Pro',
  fontSecondary: 'Lora',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Lora&family=Source+Sans+Pro:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/afg/afg-background.jpg")',
  favicon: '/static/whitelabels/afg/afg-favicon.png',
  liveSubDomain: 'afg',
  sandboxSubDomain: 'afg',
};

const BLA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bla',
  readable: 'Blacktower FM',
  logo: '/static/whitelabels/bla/bla-logo.png',
  logoType: '/static/whitelabels/bla/bla-logo.png',
  colourPrimary: '#202F3A',
  colourSecondary: '#0053B7',
  colourLight: '#0053B7dd',
  colourLighter: '#0053B715',
  buttonColour: '#202F3A',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#202F3A',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Lato',
  fontSecondary: 'Playfair Display',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&family=Playfair+Display&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/bla/bla-background.jpg")',
  favicon: '/static/whitelabels/bla/bla-favicon.png',
  liveSubDomain: 'blacktowerfm',
  sandboxSubDomain: 'bla',
};

const BLC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'blc',
  readable: 'Blacktower FM Cyprus',
  logo: '/static/whitelabels/bla/bla-logo.png',
  logoType: '/static/whitelabels/bla/bla-logo.png',
  colourPrimary: '#202F3A',
  colourSecondary: '#0053B7',
  colourLight: '#0053B7dd',
  colourLighter: '#0053B715',
  buttonColour: '#202F3A',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#202F3A',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Lato',
  fontSecondary: 'Playfair Display',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&family=Playfair+Display&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/bla/bla-background.jpg")',
  favicon: '/static/whitelabels/bla/bla-favicon.png',
  liveSubDomain: 'blc',
  sandboxSubDomain: 'blc',
};

const BUK: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'buk',
  readable: 'Blacktower Financial Management LTD (UK)',
  logo: '/static/whitelabels/bla/bla-logo.png',
  logoType: '/static/whitelabels/bla/bla-logo.png',
  colourPrimary: '#202F3A',
  colourSecondary: '#0053B7',
  colourLight: '#0053B7dd',
  colourLighter: '#0053B715',
  buttonColour: '#202F3A',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#202F3A',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Lato',
  fontSecondary: 'Playfair Display',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&family=Playfair+Display&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/bla/bla-background.jpg")',
  favicon: '/static/whitelabels/bla/bla-favicon.png',
  liveSubDomain: 'buk',
  sandboxSubDomain: 'buk',
};

const BWM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bwm',
  readable: 'Belgravia Wealth Management',
  logo: '/static/whitelabels/bwm/bwm-logo.png',
  logoType: '/static/whitelabels/bwm/bwm-logo.png',
  colourPrimary: '#001447',
  colourSecondary: '#0067dd',
  colourLight: '#0067dddd',
  colourLighter: '#0067dd15',
  buttonColour: '#001447',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#001447',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Montserrat',
  fontSecondary: 'Open Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&family=Open+Sans:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/bwm/bwm-background.jpeg")',
  favicon: '/static/whitelabels/bwm/bwm-favicon.png',
  liveSubDomain: 'belgravia-wealth-management',
  sandboxSubDomain: 'bwm',
};

const CAP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cap',
  readable: 'Capstone Financial',
  logo: '/static/whitelabels/cap/cap-logo.png',
  logoType: '/static/whitelabels/cap/cap-logo.png',
  colourPrimary: '#43525a',
  colourSecondary: '#4486ac',
  colourLight: '#4486acdd',
  colourLighter: '#4486ac15',
  buttonColour: '#43525a',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#43525a',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/cap/cap-background.jpg")',
  favicon: '/static/whitelabels/cap/cap-favicon.png',
  liveSubDomain: 'capstone',
  sandboxSubDomain: 'cap',
};

const CCM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ccm',
  readable: 'Crest Capital Management',
  logo: '/static/whitelabels/ccm/ccm-logo.png',
  logoType: '/static/whitelabels/ccm/ccm-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#01178C',
  colourLight: '#01178Cdd',
  colourLighter: '#01178C15',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#01178C',
  favicon: '/static/whitelabels/ccm/ccm-favicon.png',
  liveSubDomain: 'ccm',
  sandboxSubDomain: 'ccm',
};

const CGI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cgi',
  readable: 'Citadel Global Investments',
  logo: '/static/whitelabels/cgi/cgi-logo.png',
  logoType: '/static/whitelabels/cgi/cgi-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#449898',
  colourLight: '#449898dd',
  colourLighter: '#44989815',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#449898',
  favicon: '/static/whitelabels/cgi/cgi-favicon.png',
  liveSubDomain: 'citadel',
  sandboxSubDomain: 'cgi',
};

const CHA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cha',
  readable: 'Chartered-Advice',
  logo: '/static/whitelabels/cha/cha-logo.png',
  logoType: '/static/whitelabels/cha/cha-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#454545',
  colourLight: '#454545dd',
  colourLighter: '#45454515',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#cccccc',
  favicon: '/static/whitelabels/cha/cha-favicon.png',
  liveSubDomain: 'cha',
  sandboxSubDomain: 'cha',
};

const CIC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cic',
  readable: 'Capital Investment Consultants',
  logo: '/static/whitelabels/cic/cic-logo.png',
  logoType: '/static/whitelabels/cic/cic-logo.png',
  colourPrimary: '#444',
  colourSecondary: '#2A8B54',
  colourLight: '#2A8B54dd',
  colourLighter: '#2A8B5415',
  buttonColour: '#444',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#444',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#2A8B54',
  favicon: '/static/whitelabels/cic/cic-favicon.png',
  liveSubDomain: 'capinvesco',
  sandboxSubDomain: 'cic',
};

const CPV: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cpv',
  readable: 'Capvest',
  logo: '/static/whitelabels/cpv/cpv-logo.png',
  logoType: '/static/whitelabels/cpv/cpv-logo.png',
  colourPrimary: '#01004e',
  colourSecondary: '#d63d41',
  colourLight: '#d63d41dd',
  colourLighter: '#d63d4115',
  buttonColour: '#01004e',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#01004e',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Arimo',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Arimo:wght@400;500;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/cpv/cpv-background.jpg")',
  favicon: '/static/whitelabels/cpv/cpv-favicon.png',
  liveSubDomain: 'capvest',
  sandboxSubDomain: 'cpv',
};

const CRE: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cre',
  readable: 'Credence International Financial Consultancy LLC',
  logo: '/static/whitelabels/cre/cre-logo.png',
  logoType: '/static/whitelabels/cre/cre-logo.png',
  colourPrimary: '#012c2f',
  colourSecondary: '#8e7b56',
  colourLight: '#8e7b56dd',
  colourLighter: '#8e7b5615',
  buttonColour: '#012c2f',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#012c2f',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/cre/cre-background.jpg")',
  favicon: '/static/whitelabels/cre/cre-favicon.png',
  liveSubDomain: 'cre',
  sandboxSubDomain: 'cre',
};

const CSJ: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'csj',
  readable: 'CS Jones',
  logo: '/static/whitelabels/csj/csj-logo.png',
  logoType: '/static/whitelabels/csj/csj-logo.png',
  colourPrimary: '#901235',
  colourSecondary: '#1C252C',
  colourLight: '#1C252C',
  colourLighter: '#901235',
  buttonColour: '#901235',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#901235',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#1C252C',
  favicon: '/static/whitelabels/csj/csj-favicon.png',
  liveSubDomain: 'csj',
  sandboxSubDomain: 'csj',
};

const EPA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'epa',
  readable: 'Expertos Patrimoniales',
  logo: '/static/whitelabels/epa/epa-logo.png',
  logoType: '/static/whitelabels/epa/epa-logo.png',
  colourPrimary: '#001146',
  colourSecondary: '#AFAB9B',
  colourLight: '#AFAB9Bdd',
  colourLighter: '#AFAB9B15',
  buttonColour: '#001146',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#001146',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Rubik',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/epa/epa-background.jpg")',
  favicon: '/static/whitelabels/epa/epa-favicon.png',
  liveSubDomain: 'epa',
  sandboxSubDomain: 'epa',
};

const EQI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'eqi',
  readable: 'Easy Qrops International',
  logo: '/static/whitelabels/eqi/eqi-logo.png',
  logoType: '/static/whitelabels/eqi/eqi-logo.png',
  colourPrimary: '#215868',
  colourSecondary: '#E36C0A',
  colourLight: '#E36C0Add',
  colourLighter: '#E36C0A15',
  buttonColour: '#215868',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#215868',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#E36C0A',
  favicon: '/static/whitelabels/eqi/eqi-favicon.png',
  liveSubDomain: 'easyqrops',
  sandboxSubDomain: 'eqi',
};

const EWL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ewl',
  readable: 'Excelsior Worldwide Limited',
  logo: '/static/whitelabels/ewl/ewl-logo.png',
  logoType: '/static/whitelabels/ewl/ewl-logo.png',
  colourPrimary: '#555',
  colourSecondary: '#297fd5',
  colourLight: '#297fd5dd',
  colourLighter: '#297fd515',
  buttonColour: '#555',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#555',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Poppins',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/ewl/ewl-background.jpg")',
  favicon: '/static/whitelabels/ewl/ewl-favicon.png',
  liveSubDomain: 'ewl',
  sandboxSubDomain: 'ewl',
};

const FFC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ffc',
  readable: 'Forbears Freedom Wealth',
  logo: '/static/whitelabels/ffc/ffc-logo.png',
  logoType: '/static/whitelabels/ffc/ffc-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#0673ba',
  colourLight: '#0673badd',
  colourLighter: '#0673ba15',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#0673ba',
  favicon: '/static/whitelabels/ffc/ffc-favicon.png',
  liveSubDomain: 'forbearsfreedomchoice',
  sandboxSubDomain: 'ffc',
};

const FIT: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fit',
  readable: 'Alpha Capital International',
  logo: '/static/whitelabels/fit/fit-logo.png',
  logoType: '/static/whitelabels/fit/fit-logo.png',
  colourPrimary: '#454545',
  colourSecondary: '#B39347',
  colourLight: '#B39347dd',
  colourLighter: '#B3934715',
  buttonColour: '#454545',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#454545',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/fit/fit-background.jpg")',
  favicon: '/static/whitelabels/fit/fit-favicon.png',
  liveSubDomain: 'fi-tion',
  sandboxSubDomain: 'fit',
};

const FMA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fma',
  readable: 'Astute Financial Management Associates',
  logo: '/static/whitelabels/fma/fma-logo.png',
  logoType: '/static/whitelabels/fma/fma-logo.png',
  colourPrimary: '#1e2053',
  colourSecondary: '#47c3c5',
  colourLight: '#1e2053dd',
  colourLighter: '#1e205315',
  buttonColour: '#1e2053',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1e2053',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Fira Sans',
  fontSecondary: 'Karla',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Fira+Sans:wght@300;400;600&family=Karla:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/fma/fma-background.jpg")',
  favicon: '/static/whitelabels/fma/fma-favicon.png',
  liveSubDomain: 'mfa',
  sandboxSubDomain: 'fma',
};

const FRD: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'frd',
  readable: 'FARRINGDON ASSET MANAGEMENT',
  logo: '/static/whitelabels/frd/frd-logo.png',
  logoType: '/static/whitelabels/frd/frd-logo.png',
  colourPrimary: '#262D72',
  colourSecondary: '#9D9D9D',
  colourLight: '#9D9D9Ddd',
  colourLighter: '#9D9D9D15',
  buttonColour: '#262D72',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#262D72',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Montserrat',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/frd/frd-background.jpg")',
  favicon: '/static/whitelabels/frd/frd-favicon.png',
  liveSubDomain: 'farringdon',
  sandboxSubDomain: 'frd',
};

const FSN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fsn',
  readable: 'Financial Services Network Ltd',
  logo: '/static/whitelabels/fsn/fsn-logo.png',
  logoType: '/static/whitelabels/fsn/fsn-logo.png',
  colourPrimary: '#16223c',
  colourSecondary: '#0164C3',
  colourLight: '#16223cdd',
  colourLighter: '#16223c15',
  buttonColour: '#16223c',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#16223c',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/fsn/fsn-background.png")',
  favicon: '/static/whitelabels/fsn/fsn-favicon.png',
  liveSubDomain: 'fsn',
  sandboxSubDomain: 'fsn',
};

const GAG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gag',
  readable: 'Gulf & Global LLC',
  logo: '/static/whitelabels/gag/gag-logo.png',
  logoType: '/static/whitelabels/gag/gag-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#0673ba',
  colourLight: '#0673badd',
  colourLighter: '#0673ba15',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#0673ba',
  favicon: '/static/whitelabels/gag/gag-favicon.png',
  liveSubDomain: 'gulfandglobal',
  sandboxSubDomain: 'gag',
};

const KBP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'kbp',
  readable: 'Knightsbridge Place',
  logo: '/static/whitelabels/kbp/kbp-logo.png',
  logoType: '/static/whitelabels/kbp/kbp-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#052c5f',
  colourLight: '#052c5fdd',
  colourLighter: '#052c5f15',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Poppins',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/kbp/kbp-background.jpg")',
  favicon: '/static/whitelabels/kbp/kbp-favicon.png',
  liveSubDomain: 'kbp',
  sandboxSubDomain: 'kbp',
};

const OIB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'oib',
  readable: 'OIBME ASIA LTD',
  logo: '/static/whitelabels/oib/oib-logo.png',
  logoType: '/static/whitelabels/oib/oib-logo.png',
  colourPrimary: '#154767',
  colourSecondary: '#cbb797',
  colourLight: '#cbb797dd',
  colourLighter: '#cbb79715',
  buttonColour: '#154767',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#444',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/oib/oib-background.jpg")',
  favicon: '/static/whitelabels/oib/oib-favicon.png',
  liveSubDomain: 'oib',
  sandboxSubDomain: 'oib',
};

const OYS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'oys',
  readable: 'Oyster Bay',
  logo: '/static/whitelabels/oys/oys-logo.png',
  logoType: '/static/whitelabels/oys/oys-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#619B59',
  colourLight: '#619B59dd',
  colourLighter: '#619B5915',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Ubuntu',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Ubuntu:wght@300;400;500&display=swap',
  heroBg: '',
  loginBg: '#619B59',
  favicon: '/static/whitelabels/oys/oys-favicon.png',
  liveSubDomain: 'oysterbay',
  sandboxSubDomain: 'oys',
};

const SEA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sea',
  readable: 'South East Asia Capital',
  logo: '/static/whitelabels/sea/sea-logo.png',
  logoType: '/static/whitelabels/sea/sea-logo.png',
  colourPrimary: '#000',
  colourSecondary: '#086838',
  colourLight: '#086838dd',
  colourLighter: '#08683815',
  buttonColour: '#000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#086838',
  favicon: '/static/whitelabels/sea/sea-favicon.png',
  liveSubDomain: 'sea',
  sandboxSubDomain: 'sea',
};

const TFL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'tfl',
  readable: 'Titan Financial Ltd',
  logo: '/static/whitelabels/tfl/tfl-logo.png',
  logoType: '/static/whitelabels/tfl/tfl-logo.png',
  colourPrimary: '#111111',
  colourSecondary: '#B7201C',
  colourLight: '#B7201Cdd',
  colourLighter: '#B7201C15',
  buttonColour: '#111111',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#111111',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/tfl/tfl-background.jpg")',
  favicon: '/static/whitelabels/tfl/tfl-favicon.png',
  liveSubDomain: 'tfl',
  sandboxSubDomain: 'tfl',
};

const TWM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'twm',
  readable: 'Total Wealth Management',
  logo: '/static/whitelabels/twm/twm-logo.png',
  logoType: '/static/whitelabels/twm/twm-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#016DB5',
  colourLight: '#016DB5dd',
  colourLighter: '#016DB515',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#016DB5',
  favicon: '/static/whitelabels/twm/twm-favicon.png',
  liveSubDomain: 'twm',
  sandboxSubDomain: 'twm',
};

const WIN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'win',
  readable: 'Winson Capital',
  logo: '/static/whitelabels/win/win-logo.png',
  logoType: '/static/whitelabels/win/win-logo.png',
  colourPrimary: '#323e48',
  colourSecondary: '#b9bb18',
  colourLight: '#323e48dd',
  colourLighter: '#323e4815',
  buttonColour: '#323e48',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#323e48',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Lato',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/win/win-background.jpg")',
  favicon: '/static/whitelabels/win/win-favicon.png',
  liveSubDomain: 'win',
  sandboxSubDomain: 'win',
};

const GIG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gig',
  readable: 'Global Investors Group',
  logo: '/static/whitelabels/gig/gig-logo.jpg',
  logoType: '/static/whitelabels/gig/gig-logo.jpg',
  colourPrimary: '#333',
  colourSecondary: '#2DAAE1',
  colourLight: '#2DAAE1dd',
  colourLighter: '#2DAAE115',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#2DAAE1',
  favicon: '/static/whitelabels/gig/gig-favicon.png',
  liveSubDomain: 'globalinvestorsgroup',
  sandboxSubDomain: 'gig',
};

const GUI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gui',
  readable: 'Guardian International',
  logo: '/static/whitelabels/gui/gui-logo.png',
  logoType: '/static/whitelabels/gui/gui-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#D7BC48',
  colourLight: '#D7BC48dd',
  colourLighter: '#D7BC4815',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/gui/gui-background.jpg")',
  favicon: '/static/whitelabels/gui/gui-favicon.png',
  liveSubDomain: 'gifp-ltd',
  sandboxSubDomain: 'gui',
};

const GWM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gwm',
  readable: 'Global Wealth Management',
  logo: '/static/whitelabels/gwm/gwm-logo.jpg',
  logoType: '/static/whitelabels/gwm/gwm-logo.jpg',
  colourPrimary: '#333',
  colourSecondary: '#f6a905',
  colourLight: '#f6a905dd',
  colourLighter: '#f6a90515',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/gwm/gwm-background.jpg")',
  favicon: '/static/whitelabels/gwm/gwm-favicon.png',
  liveSubDomain: 'gwm-ltd',
  sandboxSubDomain: 'gwm',
};

const HBS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hbs',
  readable: 'Holborn South Africa',
  logo: '/static/whitelabels/hbs/hbs-logo.png',
  logoType: '/static/whitelabels/hbs/hbs-logo.png',
  colourPrimary: '#023660',
  colourSecondary: '#80B6E2',
  colourLight: '#023660dd',
  colourLighter: '#02366015',
  buttonColour: '#023660',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#023660',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/hbs/hbs-background.jpg")',
  favicon: '/static/whitelabels/hbs/hbs-favicon.png',
  liveSubDomain: 'hbs',
  sandboxSubDomain: 'hbs',
};

const HCM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hcm',
  readable: 'Hoxton Capital Management',
  logo: '/static/whitelabels/hcm/hcm-logo.png',
  logoType: '/static/whitelabels/hcm/hcm-logo.png',
  colourPrimary: '#30366A',
  colourSecondary: '#AFB0B1',
  colourLight: '#30366Add',
  colourLighter: '#30366A15',
  buttonColour: '#30366A',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#30366A',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/hcm/hcm-background.jpg")',
  favicon: '/static/whitelabels/hcm/hcm-favicon.png',
  liveSubDomain: 'hcm',
  sandboxSubDomain: 'hcm',
};

const ICH: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ich',
  readable: 'Ichor International Investments',
  logo: '/static/whitelabels/ich/ich-logo.png',
  logoType: '/static/whitelabels/ich/ich-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#FD6244',
  colourLight: '#333333dd',
  colourLighter: '#33333315',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#FD6244',
  favicon: '/static/whitelabels/ich/ich-favicon.png',
  liveSubDomain: 'ichor',
  sandboxSubDomain: 'ich',
};

const IMG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'img',
  readable: 'International Investment Management Group',
  logo: '/static/whitelabels/img/img-logo.png',
  logoType: '/static/whitelabels/img/img-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#003c60',
  colourLight: '#333333dd',
  colourLighter: '#33333315',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Oxygen',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Oxygen:wght@300;400;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/img/img-background.jpg")',
  favicon: '/static/whitelabels/img/img-favicon.png',
  liveSubDomain: 'iimg',
  sandboxSubDomain: 'img',
};

const KPI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'kpi',
  readable: 'Kaplan International',
  logo: '/static/whitelabels/kpi/kpi-logo.png',
  logoType: '/static/whitelabels/kpi/kpi-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#A37918',
  colourLight: '#A37918dd',
  colourLighter: '#A3791815',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#fed473',
  favicon: '/static/whitelabels/kpi/kpi-favicon.png',
  liveSubDomain: 'kaplan',
  sandboxSubDomain: 'kpi',
};

const LAA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'laa',
  readable: 'Northern Cross Wealth Management',
  logo: '/static/whitelabels/laa/laa-logo.png',
  logoType: '/static/whitelabels/laa/laa-logomark.png',
  colourPrimary: '#C6282E',
  colourSecondary: '#C6282E',
  colourLight: '#C6282Edd',
  colourLighter: '#C6282E15',
  buttonColour: '#000000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Quicksand',
  fontSecondary: 'Quicksand',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;600;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/laa/laa-background.jpg")',
  favicon: '/static/whitelabels/laa/laa-favicon.png',
  liveSubDomain: 'laneandassociates',
  sandboxSubDomain: 'laa',
};

const LAT: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'lat',
  readable: 'Latitude',
  logo: '/static/whitelabels/lat/lat-logo.png',
  logoType: '/static/whitelabels/lat/lat-logo.png',
  colourPrimary: '#40638e',
  colourSecondary: '#272727',
  colourLight: '#40638edd',
  colourLighter: '#40638e15',
  buttonColour: '#272727',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#272727',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Raleway',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/lat/lat-background.jpg")',
  favicon: '/static/whitelabels/lat/lat-favicon.png',
  liveSubDomain: 'lat',
  sandboxSubDomain: 'lat',
};

const LSS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'lss',
  readable: 'Lead Solutions',
  logo: '/static/whitelabels/lss/lss-logo.png',
  logoType: '/static/whitelabels/lss/lss-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#EB2832',
  colourLight: '#EB2832dd',
  colourLighter: '#EB283215',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Cardo',
  fontSecondary: 'Quattrocento',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Cardo:wght@400;700&family=Quattrocento:wght@400;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/lss/lss-background.jpg")',
  favicon: '/static/whitelabels/lss/lss-favicon.png',
  liveSubDomain: 'lss',
  sandboxSubDomain: 'lss',
};

const LWI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'lwi',
  readable: 'LWM International',
  logo: '/static/whitelabels/lwi/lwi-logo.png',
  logoType: '/static/whitelabels/lwi/lwi-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#008d36',
  colourLight: '#008d36dd',
  colourLighter: '#008d3615',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#008d36',
  favicon: '/static/whitelabels/lwi/lwi-favicon.png',
  liveSubDomain: 'lwi',
  sandboxSubDomain: 'lwi',
};

const MQR: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mqr',
  readable: 'MyQROPS',
  logo: '/static/whitelabels/mqr/mqr-logo.png',
  logoType: '/static/whitelabels/mqr/mqr-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#0574BA',
  colourLight: '#0574BAdd',
  colourLighter: '#0574BA15',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#0574BA',
  favicon: '/static/whitelabels/mqr/mqr-favicon.png',
  liveSubDomain: 'myqrops',
  sandboxSubDomain: 'mqr',
};

const NFS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'nfs',
  readable: 'Network Financial Services Ltd',
  logo: '/static/whitelabels/nfs/nfs-logo.png',
  logoType: '/static/whitelabels/nfs/nfs-logo.png',
  colourPrimary: '#16223c',
  colourSecondary: '#A7A9AC',
  colourLight: '#A7A9ACdd',
  colourLighter: '#A7A9AC15',
  buttonColour: '#16223c',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#16223c',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: 'Roboto',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600&family=Roboto:wght@300;400;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/nfs/nfs-background.jpg")',
  favicon: '/static/whitelabels/nfs/nfs-favicon.png',
  liveSubDomain: 'nfs',
  sandboxSubDomain: 'nfs',
};

const OPF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'opf',
  readable: 'Opes Fidelio',
  logo: '/static/whitelabels/opf/opf-logo.png',
  logoType: '/static/whitelabels/opf/opf-logo.png',
  colourPrimary: '#444',
  colourSecondary: '#92209d',
  colourLight: '#92209ddd',
  colourLighter: '#92209d15',
  buttonColour: '#444',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#444',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/opf/opf-background.jpg")',
  favicon: '/static/whitelabels/opf/opf-favicon.png',
  liveSubDomain: 'opf',
  sandboxSubDomain: 'opf',
};

const PAM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pam',
  readable: 'Pecunia Asset Management',
  logo: '/static/whitelabels/pam/pam-logo.png',
  logoType: '/static/whitelabels/pam/pam-logo.png',
  colourPrimary: '#174c81',
  colourSecondary: '#18ba60',
  colourLight: '#18ba60dd',
  colourLighter: '#18ba6015',
  buttonColour: '#174c81',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#174c81',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Hind Siliguri',
  fontSecondary: 'Hind Vadodara',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@300;400;600&family=Hind+Vadodara:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/pam/pam-background.jpg")',
  favicon: '/static/whitelabels/pam/pam-favicon.png',
  liveSubDomain: 'pecunia',
  sandboxSubDomain: 'pam',
};

const PFA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pfa',
  readable: 'Prime Financial Advisors',
  logo: '/static/whitelabels/pfa/pfa-logo.png',
  logoType: '/static/whitelabels/pfa/pfa-logo.png',
  colourPrimary: '#083e55',
  colourSecondary: '#4CC9E8',
  colourLight: '#4CC9E8dd',
  colourLighter: '#14CC9E815',
  buttonColour: '#083e55',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#083e55',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Montserrat',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/pfa/pfa-background.jpg")',
  favicon: '/static/whitelabels/pfa/pfa-favicon.png',
  liveSubDomain: 'primefanetwork',
  sandboxSubDomain: 'pfa',
};

const PRE: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pre',
  readable: 'Premium Finance',
  logo: '/static/whitelabels/pre/pre-logo.png',
  logoType: '/static/whitelabels/pre/pre-logo.png',
  colourPrimary: '#555',
  colourSecondary: '#544d84',
  colourLight: '#544d84dd',
  colourLighter: '#544d8415',
  buttonColour: '#555',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#555',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Montserrat',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: '#544d84',
  favicon: '/static/whitelabels/pre/pre-favicon.png',
  liveSubDomain: 'premiumfinancial',
  sandboxSubDomain: 'pre',
};

const QAS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'qas',
  readable: 'QROPS Advisory Services',
  logo: '/static/whitelabels/qas/qas-logo.png',
  logoType: '/static/whitelabels/qas/qas-logo.png',
  colourPrimary: '#333333',
  colourSecondary: '#63AFDD',
  colourLight: '#63AFDDdd',
  colourLighter: '#63AFDD15',
  buttonColour: '#333333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#63AFDD',
  favicon: '/static/whitelabels/qas/qas-favicon.png',
  liveSubDomain: 'qas',
  sandboxSubDomain: 'qas',
};

const REI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'rei',
  readable: 'Richelieu Wealth Solutions',
  logo: '/static/whitelabels/rei/rei-logo.png',
  logoType: '/static/whitelabels/rei/rei-logo.png',
  colourPrimary: '#0E132F',
  colourSecondary: '#98AFD8',
  colourLight: '#98AFD8dd',
  colourLighter: '#98AFD815',
  buttonColour: '#0E132F',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#0E132F',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Montserrat',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/rei/rei-background.jpg")',
  favicon: '/static/whitelabels/rei/rei-favicon.png',
  liveSubDomain: 'rei',
  sandboxSubDomain: 'rei',
};

const SAL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sal',
  readable: 'Sovereign Atlantica Lite',
  logo: '/static/whitelabels/sal/sal-logo.png',
  logoType: '/static/whitelabels/sal/sal-logo.png',
  colourPrimary: '#000000',
  colourSecondary: '#dc3931',
  colourLight: '#dc3931dd',
  colourLighter: '#dc393115',
  buttonColour: '#000000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/sal/sal-background.jpg")',
  favicon: '/static/whitelabels/sal/sal-favicon.png',
  liveSubDomain: 'samatlanticalite',
  sandboxSubDomain: 'sal',
};

const SAM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sam',
  readable: 'Sovereign Asset Management',
  logo: '/static/whitelabels/sam/sam-logo.png',
  logoType: '/static/whitelabels/sam/sam-logo.png',
  colourPrimary: '#000000',
  colourSecondary: '#dc3931',
  colourLight: '#dc3931dd',
  colourLighter: '#dc393115',
  buttonColour: '#000000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/sam/sam-background.jpg")',
  favicon: '/static/whitelabels/sam/sam-favicon.png',
  liveSubDomain: 'sam',
  sandboxSubDomain: 'sam',
};

const SIM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sim',
  readable: 'Simplicity Wealth',
  logo: '/static/whitelabels/sim/sim-logo.png',
  logoType: '/static/whitelabels/sim/sim-logo.png',
  colourPrimary: '#3f5473',
  colourSecondary: '#C09350',
  colourLight: '#C09350dd',
  colourLighter: '#C0935015',
  buttonColour: '#3f5473',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#3f5473',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Montserrat',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/sim/sim-background.jpg")',
  favicon: '/static/whitelabels/sim/sim-favicon.png',
  liveSubDomain: 'sim',
  sandboxSubDomain: 'sim',
};

const SIP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sip',
  readable: 'SIPP',
  logo: '/static/whitelabels/sip/sip-logo.png',
  logoType: '/static/whitelabels/sip/sip-logo.png',
  colourPrimary: '#1A3A6F',
  colourSecondary: '#F4D800',
  colourLight: '#1A3A6Fdd',
  colourLighter: '#1A3A6F15',
  buttonColour: '#1A3A6F',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1A3A6F',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/sip/sip-background.jpg")',
  favicon: '/static/whitelabels/sip/sip-favicon.png',
  liveSubDomain: 'sip',
  sandboxSubDomain: 'sip',
};

const THG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'thg',
  readable: "St. James's Place Wealth Management",
  logo: '/static/whitelabels/thg/thg-logo.jpg',
  logoType: '/static/whitelabels/thg/thg-logo.jpg',
  colourPrimary: '#003B80',
  colourSecondary: '#9A8348',
  colourLight: '#9A8348dd',
  colourLighter: '#9A834815',
  buttonColour: '#003B80',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003B80',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#9A8348',
  favicon: '/static/whitelabels/thg/thg-favicon.png',
  liveSubDomain: 'thehenleygroup',
  sandboxSubDomain: 'thg',
};

const USU: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'usu',
  readable: 'USUS',
  logo: '/static/whitelabels/usu/usu-logo.png',
  logoType: '/static/whitelabels/usu/usu-logo.png',
  colourPrimary: '#333',
  colourSecondary: '#ED8701',
  colourLight: '#ED8701dd',
  colourLighter: '#ED870115',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#ED8701',
  favicon: '/static/whitelabels/usu/usu-favicon.png',
  liveSubDomain: 'usus',
  sandboxSubDomain: 'usu',
};

const STC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'stc',
  readable: 'Stein Consulting LTD',
  logo: '/static/whitelabels/stc/stc-logo.png',
  logoType: '/static/whitelabels/stc/stc-logo.png',
  colourPrimary: '#000000',
  colourSecondary: '#bd935e',
  colourLight: '#bd935edd',
  colourLighter: '#bd935e15',
  buttonColour: '#000000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/stc/stc-background.jpg")',
  favicon: '/static/whitelabels/stc/stc-favicon.png',
  liveSubDomain: 'stc',
  sandboxSubDomain: 'stc',
};

const TRU: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'tru',
  readable: 'Truestone Capital',
  logo: '/static/whitelabels/tru/tru-logo.png',
  logoType: '/static/whitelabels/tru/tru-logo.png',
  colourPrimary: '#111',
  colourSecondary: '#a78316',
  colourLight: '#a78316dd',
  colourLighter: '#a7831615',
  buttonColour: '#111',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#111',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Source Sans Pro',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/tru/tru-background.jpg")',
  favicon: '/static/whitelabels/tru/tru-favicon.png',
  liveSubDomain: 'tru',
  sandboxSubDomain: 'tru',
};

const BCI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bci',
  readable: 'Barclay Carrigan International',
  logo: '/static/whitelabels/bci/bci-logo.png',
  logoType: '/static/whitelabels/bci/bci-logo.png',
  colourPrimary: '#444444',
  colourSecondary: '#0095eb',
  colourLight: '#0095ebdd',
  colourLighter: '#0095eb15',
  buttonColour: '#444444',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#444444',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/bci/bci-background.jpg")',
  favicon: '/static/whitelabels/bci/bci-favicon.png',
  liveSubDomain: 'bci',
  sandboxSubDomain: 'bci',
};

const CPS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cps',
  readable: 'Capital Platforms',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'cps',
  sandboxSubDomain: 'cps',
};

const IPP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ipp',
  readable: 'IPP',
  logo: '',
  logoType: '',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '#003479',
  favicon: '',
  liveSubDomain: 'ipp',
  sandboxSubDomain: 'ipp',
};

const NEBO: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'nebo',
  readable: 'M1 Malaysia',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'nebo',
  sandboxSubDomain: 'nebo',
};

const NEW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'new',
  readable: 'NewNet Consulting Ltd',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#00254b',
  colourSecondary: '#00254b',
  colourLight: '#00254bdd',
  colourLighter: '#00254b15',
  buttonColour: '#00254b',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#00254b',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'new',
  sandboxSubDomain: 'new',
};

const SBC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sbc',
  readable: 'Sumida Brokers & Consulting',
  logo: '',
  logoType: '',
  colourPrimary: '#00254b',
  colourSecondary: '#00254b',
  colourLight: '#00254bdd',
  colourLighter: '#00254b15',
  buttonColour: '#00254b',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#00254b',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '',
  liveSubDomain: 'sbc',
  sandboxSubDomain: 'sbc',
};

const WMG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'wmg',
  readable: 'Wealth Management Group Limited',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'wmg',
  sandboxSubDomain: 'wmg',
};

const GWS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gws',
  readable: 'Skybound Wealth Management',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'gws',
  sandboxSubDomain: 'gws',
};

const LEG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'leg',
  readable: 'Legacy',
  logo: '',
  logoType: '',
  colourPrimary: '#111111',
  colourSecondary: '#111111',
  colourLight: '#111111dd',
  colourLighter: '#11111115',
  buttonColour: '#111111',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#111111',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '#111111',
  favicon: '',
  liveSubDomain: 'leg',
  sandboxSubDomain: 'leg',
};

const MPV: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mpv',
  readable: 'MPV International',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'mpv',
  sandboxSubDomain: 'mpv',
};

const LAW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'law',
  readable: 'Lawsons Network AG',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'law',
  sandboxSubDomain: 'law',
};

const ORE: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ore',
  readable: 'Oreana Financial Services Limited',
  logo: '/static/whitelabels/ore/ore-logo.png',
  logoType: '/static/whitelabels/ore/ore-logo.png',
  colourPrimary: '#000000',
  colourSecondary: '#f4121b',
  colourLight: '#f4121bdd',
  colourLighter: '#f4121b15',
  buttonColour: '#000000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/ore/ore-background.jpg")',
  favicon: '/static/whitelabels/ore/ore-favicon.png',
  liveSubDomain: 'ore',
  sandboxSubDomain: 'ore',
};

const TMG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'tmg',
  readable: 'The MyGroup',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'tmg',
  sandboxSubDomain: 'tmg',
};

const AIS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ais',
  readable: 'Aisa Financial Planning',
  logo: '/static/whitelabels/ais/ais-logo.png',
  logoType: '/static/whitelabels/ais/ais-logo.png',
  colourPrimary: '#272727',
  colourSecondary: '#fd4f00',
  colourLight: '#fd4f00dd',
  colourLighter: '#fd4f0015',
  buttonColour: '#272727',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#272727',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Source Sans Pro',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/ais/ais-background.jpg")',
  favicon: '/static/whitelabels/ais/ais-favicon.png',
  liveSubDomain: 'ais',
  sandboxSubDomain: 'ais',
};

const ADF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'adf',
  readable: 'International AMG Ltd',
  logo: '/static/whitelabels/adf/adf-logo.png',
  logoType: '/static/whitelabels/adf/adf-logo.png',
  colourPrimary: '#000000',
  colourSecondary: '#F43042',
  colourLight: '#F43042dd',
  colourLighter: '#F4304215',
  buttonColour: '#000000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/adf/adf-background.jpg")',
  favicon: '/static/whitelabels/adf/adf-favicon.png',
  liveSubDomain: 'adf',
  sandboxSubDomain: 'adf',
};

const GIS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gis',
  readable: 'Global Investment Solutions Mauritius Ltd',
  logo: '/static/whitelabels/adf/adf-logo.png',
  logoType: '/static/whitelabels/adf/adf-logo.png',
  colourPrimary: '#000000',
  colourSecondary: '#F43042',
  colourLight: '#F43042dd',
  colourLighter: '#F4304215',
  buttonColour: '#000000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: '',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/adf/adf-background.jpg")',
  favicon: '/static/whitelabels/adf/adf-favicon.png',
  liveSubDomain: 'gis',
  sandboxSubDomain: 'gis',
};

const AGT: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'agt',
  readable: 'Argentum',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'agt',
  sandboxSubDomain: 'agt',
};

const HWW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hww',
  readable: 'Global HWW',
  logo: '',
  logoType: '',
  colourPrimary: '#00254b',
  colourSecondary: '#3984C0',
  colourLight: '#00254bdd',
  colourLighter: '#00254b15',
  buttonColour: '#00254b',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#00254b',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '#3984C0',
  favicon: '',
  liveSubDomain: 'hww',
  sandboxSubDomain: 'hww',
};

const FAR: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'far',
  readable: 'Farmond (L) Ltd',
  logo: '',
  logoType: '',
  colourPrimary: '#00254b',
  colourSecondary: '#0673ba',
  colourLight: '#00254bdd',
  colourLighter: '#00254b15',
  buttonColour: '#0673ba',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#222222',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '#0673ba',
  favicon: '',
  liveSubDomain: 'far',
  sandboxSubDomain: 'far',
};

const RGI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'rgi',
  readable: 'Regency International Ltd',
  logo: '',
  logoType: '',
  colourPrimary: '#00254b',
  colourSecondary: '#0673ba',
  colourLight: '#00254bdd',
  colourLighter: '#00254b15',
  buttonColour: '#0673ba',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#222222',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '#0673ba',
  favicon: '',
  liveSubDomain: 'rgi',
  sandboxSubDomain: 'rgi',
};

const TZN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'tzn',
  readable: 'Tenzing Pacific Services',
  logo: '/static/whitelabels/tzn/tzn-logo.png',
  logoType: '/static/whitelabels/tzn/tzn-logo.png',
  colourPrimary: '#0E2A47',
  colourSecondary: '#09a223',
  colourLight: '#369BE3dd',
  colourLighter: '#369BE315',
  buttonColour: '#000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#0e0e0e',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Nunito Sans',
  fontSecondary: 'Poppins',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;600&family=Poppins:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/tzn/tzn-background.jpeg")',
  favicon: '/static/whitelabels/tzn/tzn-favicon.png',
  liveSubDomain: 'tzn',
  sandboxSubDomain: 'tzn',
};

const CHU: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'chu',
  readable: 'Global Advice Group',
  logo: '/static/whitelabels/chu/chu-logo.png',
  logoType: '/static/whitelabels/chu/chu-logo-login.png',
  colourPrimary: '#333',
  colourSecondary: '#98B8A5',
  colourLight: '#98B8A5dd',
  colourLighter: '#98B8A515',
  buttonColour: '#333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: 'Open Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/chu/chu-background.jpg")',
  favicon: '/static/whitelabels/chu/chu-favicon.png',
  liveSubDomain: 'chu',
  sandboxSubDomain: 'chu',
};
const BAB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bab',
  readable: 'Bascar Capital',
  logo: '/static/whitelabels/bab/bab-logo.png',
  logoType: '/static/whitelabels/bab/bab-logo.png',
  colourPrimary: '#006634',
  colourSecondary: '#999965',
  colourLight: '#006634dd',
  colourLighter: '#00663415',
  buttonColour: '#999965',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#006634',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Montserrat',
  fontSecondary: 'Poppins',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/bab/bab-background.jpg")',
  favicon: '/static/whitelabels/bab/bab-favicon.png',
  liveSubDomain: 'bab',
  sandboxSubDomain: 'bab',
};

const CIS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cis',
  readable: 'Carrick Investment Services',
  logo: '/static/carrick-logo-long.png',
  logoType: '/static/carrick-logo-square.png',
  colourPrimary: '#112031',
  colourSecondary: '#AC677A',
  colourLight: '#112031dd',
  colourLighter: '#11203115',
  buttonColour: '#112031',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#112031',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Merriweather',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: '#112031',
  favicon: '/static/carrick-favicon.png',
  liveSubDomain: 'cis',
  sandboxSubDomain: 'cis',
};

const GRA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gra',
  readable: 'Amici International Corporation',
  logo: '/static/whitelabels/gra/gra-logo.png',
  logoType: '/static/whitelabels/gra/gra-logo.png',
  colourPrimary: '#111111',
  colourSecondary: '#61c0bf',
  colourLight: '#61c0bfdd',
  colourLighter: '#61c0bf15',
  buttonColour: '#111111',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#111111',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Lora',
  fontSecondary: 'Poppins',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600&family=Poppins:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: '#61c0bf',
  favicon: '/static/whitelabels/gra/gra-favicon.png',
  liveSubDomain: 'gra',
  sandboxSubDomain: 'gra',
};

const PUM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pum',
  readable: 'Pumapa',
  logo: '/static/whitelabels/pum/pum-logo.png',
  logoType: '/static/whitelabels/pum/pum-logo-sq.png',
  colourPrimary: '#333333',
  colourSecondary: '#bdbd1e',
  colourLight: '#bdbd1edd',
  colourLighter: '#bdbd1e15',
  buttonColour: '#333333',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#333333',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Cabin',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Cabin:wght@400;500;600&family=Roboto:wght@300;500;700&display=swap',
  heroBg: '',
  loginBg: '#000',
  favicon: '/static/whitelabels/pum/pum-favicon.png',
  liveSubDomain: 'pum',
  sandboxSubDomain: 'pum',
};

const CWZ: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cwz',
  readable: 'Carrick Wealth Zimbabwe',
  logo: '/static/carrick-logo-long.png',
  logoType: '/static/carrick-logo-square.png',
  colourPrimary: '#112031',
  colourSecondary: '#AC677A',
  colourLight: '#112031dd',
  colourLighter: '#11203115',
  buttonColour: '#112031',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#112031',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Merriweather',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: '#112031',
  favicon: '/static/carrick-favicon.png',
  liveSubDomain: 'cwz',
  sandboxSubDomain: 'cwz',
};

const SEV: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sev',
  readable: 'Seven Services FZ LLE',
  logo: '/static/whitelabels/sev/sev-logo.png',
  logoType: '/static/whitelabels/sev/sev-logo.png',
  colourPrimary: '#0D0F10',
  colourSecondary: '#600821',
  colourLight: '#600821dd',
  colourLighter: '#60082115',
  buttonColour: '#600821',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#0D0F10',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Poppins',
  fontSecondary: 'Hind Siliguri',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@300;500;700&family=Poppins:wght@300;500;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/sev/sev-background.jpg")',
  favicon: '/static/whitelabels/sev/sev-favicon.png',
  liveSubDomain: 'sev',
  sandboxSubDomain: 'sev',
};

const ICA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ica',
  readable: 'Integral Capital Advisory',
  logo: '/static/whitelabels/ica/ica-logo.png',
  logoType: '/static/whitelabels/ica/ica-logo.png',
  colourPrimary: '#151515',
  colourSecondary: '#194F65',
  colourLight: '#194F65dd',
  colourLighter: '#194F6515',
  buttonColour: '#194F65',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#151515',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/ica/ica-background.jpg")',
  favicon: '/static/whitelabels/ica/ica-favicon.png',
  liveSubDomain: 'ica',
  sandboxSubDomain: 'ica',
};

const AIB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'aib',
  readable: 'Armchair Insurance Brokers Limited',
  logo: '/static/whitelabels/aib/aib-logo.png',
  logoType: '/static/whitelabels/aib/aib-logo.png',
  colourPrimary: '#151515',
  colourSecondary: '#861183',
  colourLight: '#861183dd',
  colourLighter: '#86118315',
  buttonColour: '#861183',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#151515',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Poppins',
  fontSecondary: 'Poppins',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100;0,400;0,600;0,700;1,400;1,700&family=Poppins:wght@300;400;500;600&display=swap',
  heroBg: '',
  loginBg: '#861183',
  favicon: '/static/whitelabels/aib/aib-favicon.png',
  liveSubDomain: 'aib',
  sandboxSubDomain: 'aib',
};

const JSG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'jsg',
  readable: 'Just Service Global',
  logo: '/static/whitelabels/jsg/jsg-logo.png',
  logoType: '/static/whitelabels/jsg/jsg-logo.png',
  colourPrimary: '#2F2E2E',
  colourSecondary: '#0B8036',
  colourLight: '#0B8036dd',
  colourLighter: '#0B803615',
  buttonColour: '#2F2E2E',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#2F2E2E',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/jsg/jsg-background.jpg")',
  favicon: '/static/whitelabels/jsg/jsg-favicon.png',
  liveSubDomain: 'jsg',
  sandboxSubDomain: 'jsg',
};
const SYN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'syn',
  readable: 'Synergy Financial Advisors',
  logo: '/static/whitelabels/syn/syn-logo.png',
  logoType: '/static/whitelabels/syn/syn-logo.png',
  colourPrimary: '#2F2E2E',
  colourSecondary: '#06367f',
  colourLight: '#06367fdd',
  colourLighter: '#06367f15',
  buttonColour: '#06367f',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#2F2E2E',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Noto Sans',
  fontSecondary: 'Noto Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,100;0,400;0,700;1,400;1,700&display=swap',
  heroBg: '',
  loginBg: '#06367f',
  favicon: '/static/whitelabels/syn/syn-favicon.png',
  liveSubDomain: 'syn',
  sandboxSubDomain: 'syn',
};

const IND: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ind',
  readable: 'Indigo Financial Analysis & Consultancy LLC',
  logo: '/static/whitelabels/ind/ind-logo.png',
  logoType: '/static/whitelabels/ind/ind-logo.png',
  colourPrimary: '#034c84',
  colourSecondary: '#6997c0',
  colourLight: '#6997c0dd',
  colourLighter: '#6997c015',
  buttonColour: '#034c84',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#034c84',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Montserrat',
  fontSecondary: 'Montserrat',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Montserrat:wght@100;200;300;400;500;600;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/ind/ind-background.jpg")',
  favicon: '/static/whitelabels/ind/ind-favicon.png',
  liveSubDomain: 'ind',
  sandboxSubDomain: 'ind',
};

const BLR: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'blr',
  readable: 'Blue Ridge Capital',
  logo: '/static/whitelabels/blr/blr-logo.png',
  logoType: '/static/whitelabels/blr/blr-logo.png',
  colourPrimary: '#1D1B27',
  colourSecondary: '#0068B7',
  colourLight: '#0068B7dd',
  colourLighter: '#0068B715',
  buttonColour: '#1D1B27',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1D1B27',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Raleway',
  fontSecondary: 'Raleway',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Raleway:wght@300;500;600;700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/blr/blr-background.jpg")',
  favicon: '/static/whitelabels/blr/blr-favicon.png',
  liveSubDomain: 'blr',
  sandboxSubDomain: 'blr',
};

const MCG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mcg',
  readable: 'Melbourne Capital Group',
  logo: '/static/whitelabels/mcg/mcg-logo.png',
  logoType: '/static/whitelabels/mcg/mcg-logo.png',
  colourPrimary: '#1a54c6',
  colourSecondary: '#1a54c6',
  colourLight: '#0068B7dd',
  colourLighter: '#0068B715',
  buttonColour: '#1a54c6',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1a54c6',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'IBM Plex Sans',
  fontSecondary: 'IBM Plex Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/mcg/mcg-background.png")',
  favicon: '/static/whitelabels/mcg/mcg-favicon.png',
  liveSubDomain: 'mcg',
  sandboxSubDomain: 'mcg',
};

const PCC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pcc',
  readable: 'PCC Wealth',
  logo: '/static/whitelabels/pcc/pcc-logo.png',
  logoType: '/static/whitelabels/pcc/pcc-logo.png',
  colourPrimary: '#ceab6a',
  colourSecondary: '#ceab6a',
  colourLight: '#0068B7dd',
  colourLighter: '#0068B715',
  buttonColour: '#ceab6a',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#ceab6a',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'IBM Plex Sans',
  fontSecondary: 'IBM Plex Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/pcc/pcc-background.png")',
  favicon: '/static/whitelabels/pcc/pcc-favicon.png',
  liveSubDomain: 'pcc',
  sandboxSubDomain: 'pcc',
};

const AST: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ast',
  readable: 'Astraasia Group Limited',
  logo: '/static/whitelabels/ast/ast-logo.png',
  logoType: '/static/whitelabels/ast/ast-logo.png',
  colourPrimary: '#1e3175',
  colourSecondary: '#1e3175',
  colourLight: '#0068B7dd',
  colourLighter: '#0068B715',
  buttonColour: '#1e3175',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1e3175',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Arial',
  fontSecondary: 'Helvetica',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/ast/ast-background.jpg")',
  favicon: '/static/whitelabels/ast/ast-favicon.png',
  liveSubDomain: 'ast',
  sandboxSubDomain: 'ast',
};

const GPO: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gpo',
  readable: 'Global Portfolio Solutions Ltd',
  logo: '/static/whitelabels/gpo/gpo-logo.png',
  logoType: '/static/whitelabels/gpo/gpo-logo.png',
  colourPrimary: '#3b4043',
  colourSecondary: '#3b4043',
  colourLight: '#0068B7dd',
  colourLighter: '#0068B715',
  buttonColour: '#3b4043',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#3b4043',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/gpo/gpo-background.jpeg")',
  favicon: '/static/whitelabels/gpo/gpo-favicon.png',
  liveSubDomain: 'gpo',
  sandboxSubDomain: 'gpo',
};

const AGM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'agm',
  readable: 'Argentum Private Wealth',
  logo: '/static/whitelabels/agm/agm-logo.png',
  logoType: '/static/whitelabels/agm/agm-logo.png',
  colourPrimary: '#007075',
  colourSecondary: '#007075',
  colourLight: '#0068B7dd',
  colourLighter: '#0068B715',
  buttonColour: '#007075',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#007075',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Mignon',
  fontSecondary: 'Mignon',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/agm/agm-background.jpg")',
  favicon: '/static/whitelabels/agm/agm-favicon.png',
  liveSubDomain: 'agm',
  sandboxSubDomain: 'agm',
};

const APC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'apc',
  readable: 'Anchor Private Clients',
  logo: '/static/whitelabels/apc/apc-logo.jpg',
  logoType: '/static/whitelabels/apc/apc-logo.jpg',
  colourPrimary: '#0c2e4b',
  colourSecondary: '#007075',
  colourLight: '#0068B7dd',
  colourLighter: '#0068B715',
  buttonColour: '#0c2e4b',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#0c2e4b',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Lato',
  fontSecondary: 'Lato',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Lato:wght@300;400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/apc/apc-background.jpg")',
  favicon: '/static/whitelabels/apc/apc-favicon.jpg',
  liveSubDomain: 'apc',
  sandboxSubDomain: 'apc',
};

const HEC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hec',
  readable: 'Hexagone Capital Limited',
  logo: '/static/whitelabels/hec/hec-logo-new.png',
  logoType: '/static/whitelabels/hec/hec-logo-new.png',
  colourPrimary: '#324158',
  colourSecondary: '#B21E44',
  colourLight: '#B21E44dd',
  colourLighter: '#B21E4415',
  buttonColour: '#324158',
  buttonBgColour: '#EFEFEF',
  colourTitle: '#324158',
  bgColourPrimary: '#EAECEE',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: 'Libre Caslon Text',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Libre+Caslon+Text:ital,wght@0,400;0,700;1,400&family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/hexagone-login-bg.jpeg")',
  favicon:
    'https://static.wixstatic.com/media/bab916_22b9f0c3998e4b4cbb1a0d0c29a2bc10~mv2.png/v1/fill/w_32%2Ch_32%2Clg_1%2Cusm_0.66_1.00_0.01/bab916_22b9f0c3998e4b4cbb1a0d0c29a2bc10~mv2.png',
  liveSubDomain: 'hec',
  sandboxSubDomain: 'hec',
};

const BGT: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bgt',
  readable: 'Broadgate Advisory',
  logo: '/static/whitelabels/bgt/bgt-logo.png',
  logoType: '/static/whitelabels/bgt/bgt-logo.png',
  colourPrimary: '#204C9E',
  colourSecondary: '#E1EDED',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#204C9E',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#204C9E',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/bgt/bgt-background.jpg")',
  favicon: '/static/whitelabels/bgt/bgt-favicon.jpg',
  liveSubDomain: 'bgt',
  sandboxSubDomain: 'bgt',
};

const PDG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pdg',
  readable: 'Pembridge Direct',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'pdg',
  sandboxSubDomain: 'pdg',
};

const PDO: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pdo',
  readable: 'Pembridge Online',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'pdo',
  sandboxSubDomain: 'pdo',
};

const AML: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'aml',
  readable: 'Amalia Direct',
  logo: '/static/whitelabels/aml/aml-logo.png',
  logoType: '/static/whitelabels/aml/aml-logo.png',
  colourPrimary: '#00467F',
  colourSecondary: '#E1EDED',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#00467F',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#00467F',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/aml/aml-background.png")',
  favicon: '/static/whitelabels/aml/aml-favicon.png',
  liveSubDomain: 'aml',
  sandboxSubDomain: 'aml',
};

const CED: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ced',
  readable: 'Cedars IFA',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'ced',
  sandboxSubDomain: 'ced',
};

const ABD: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'abd',
  readable: 'ABD World Group',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'abd',
  sandboxSubDomain: 'abd',
};

const AIF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'aif',
  readable: 'Argent',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'aif',
  sandboxSubDomain: 'aif',
};

const IDO: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ido',
  readable: 'Invest Direct Online',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'ido',
  sandboxSubDomain: 'ido',
};

const FFP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ffp',
  readable: 'Fortune Cap',
  logo: '/static/whitelabels/ffp/ffp-logo.jpg',
  logoType: '/static/whitelabels/ffp/ffp-logo.jpg',
  colourPrimary: '#001779',
  colourSecondary: '#E1EDED',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#001779',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#001779',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/ffp/ffp-background.jpg")',
  favicon: '/static/whitelabels/ffp/ffp-favicon.png',
  liveSubDomain: 'ffp',
  sandboxSubDomain: 'ffp',
};

const BBG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bbg',
  readable: 'Berkshire Investment Services',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'bbg',
  sandboxSubDomain: 'bbg',
};

const OFS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ofs',
  readable: 'Offshore Fund Supermarket',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'ofs',
  sandboxSubDomain: 'ofs',
};

const VIO: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'vio',
  readable: 'Vestidio',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'vio',
  sandboxSubDomain: 'vio',
};

const CAM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cam',
  readable: 'Campbell Alexander',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'cam',
  sandboxSubDomain: 'cam',
};

const EAB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'eab',
  readable: 'Equity & Bond Associates',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'eab',
  sandboxSubDomain: 'eab',
};

const AMB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'amb',
  readable: 'Ambleside Consultants Ltd',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'amb',
  sandboxSubDomain: 'amb',
};

const CSM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'csm',
  readable: 'CSM Ltd',
  logo: '/static/whitelabels/csm/csm-logo.png',
  logoType: '/static/whitelabels/csm/csm-logo.png',
  colourPrimary: '#003100',
  colourSecondary: '#E1EDED',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#003100',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003100',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/csm/csm-bg.jpg")',
  favicon: '/static/whitelabels/csm/csm-favicon.png',
  liveSubDomain: 'csm',
  sandboxSubDomain: 'csm',
};

const JJI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'jji',
  readable: 'John Jenner International Limited',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'jji',
  sandboxSubDomain: 'jji',
};

const IOD: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'iod',
  readable: 'Invest Offshore Direct',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'iod',
  sandboxSubDomain: 'iod',
};

const BIG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'big',
  readable: 'Best Incentive Group',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'big',
  sandboxSubDomain: 'big',
};

const CAR: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'car',
  readable: 'Caravel Partners Ltd',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'car',
  sandboxSubDomain: 'car',
};

const CAC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cac',
  readable: 'Candour Consultancy',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'cac',
  sandboxSubDomain: 'cac',
};

const SIJ: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sij',
  readable: 'Sinclair James',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'sij',
  sandboxSubDomain: 'sij',
};

const UNI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'uni',
  readable: 'Unity Financial Partners',
  logo: '/static/whitelabels/uni/uni-logo.png',
  logoType: '/static/whitelabels/uni/uni-logo.png',
  colourPrimary: '#3A6A93',
  colourSecondary: '#E1EDED',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#3A6A93',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#3A6A93',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/uni/uni-background.jpg")',
  favicon: '/static/whitelabels/uni/uni-favicon.png',
  liveSubDomain: 'uni',
  sandboxSubDomain: 'uni',
};

const RHI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'rhi',
  readable: 'RH Investment Services Ltd',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'rhi',
  sandboxSubDomain: 'rhi',
};

const PAR: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'par',
  readable: 'Paradigm Private Clients',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'par',
  sandboxSubDomain: 'par',
};

const PHX: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'phx',
  readable: 'Phoenix Consultants LTD',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'phx',
  sandboxSubDomain: 'phx',
};

const CSL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'csl',
  readable: 'Compass Solutions Limited',
  logo: '/static/whitelabels/csl/csl-logo.png',
  logoType: '/static/whitelabels/csl/csl-logo.png',
  colourPrimary: '#016FAE',
  colourSecondary: '#E1EDED',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#016FAE',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#016FAE',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/csl/csl-background.jpg")',
  favicon: '/static/whitelabels/csl/csl-favicon.png',
  liveSubDomain: 'csl',
  sandboxSubDomain: 'csl',
};

const PFM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pfm',
  readable: 'PFM International',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'pfm',
  sandboxSubDomain: 'pfm',
};

const BDW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bdw',
  readable: 'BD Wealth Management',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'bdw',
  sandboxSubDomain: 'bdw',
};

const OPL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'opl',
  readable: 'Online Platform 247 Pte Ltd. ',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'opl',
  sandboxSubDomain: 'opl',
};

const PIC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pic',
  readable: 'PIC-Global',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'pic',
  sandboxSubDomain: 'pic',
};

const BAM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bam',
  readable: 'BAM Asset Management',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'bam',
  sandboxSubDomain: 'bam',
};

const TTG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ttg',
  readable: 'TTG Wealth Management',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'ttg',
  sandboxSubDomain: 'ttg',
};

const MIP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mip',
  readable: 'Marvelous Investments',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'mip',
  sandboxSubDomain: 'mip',
};

const CWF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cwf',
  readable: 'Castiel Winser',
  logo: '/static/whitelabels/cwf/cwf-logo.png',
  logoType: '/static/whitelabels/cwf/cwf-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/cwf/cwf-bg.jpg")',
  favicon: '/static/whitelabels/cwf/cwf-favcion.ico',
  liveSubDomain: 'cwf',
  sandboxSubDomain: 'cwf',
};

const MR: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mr',
  readable: 'MountRock',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'mr',
  sandboxSubDomain: 'mr',
};

const CAT: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cat',
  readable: 'Caterer Goodman',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'cat',
  sandboxSubDomain: 'cat',
};

const LIF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'lif',
  readable: 'LIFEADMIN',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'lif',
  sandboxSubDomain: 'lif',
};

const AAL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'aal',
  readable: 'Assured Alliance',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'aal',
  sandboxSubDomain: 'aal',
};

const LHS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'lhs',
  readable: 'Lighthouse',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'lhs',
  sandboxSubDomain: 'lhs',
};

const MFD: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mfd',
  readable: 'My Funds Direct',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'mfd',
  sandboxSubDomain: 'mfd',
};

const IDD: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'idd',
  readable: 'IDAD',
  logo: '/static/whitelabels/idd/idd-logo.png',
  logoType: '/static/whitelabels/idd/idd-logo.png',
  colourPrimary: '#40C1D6',
  colourSecondary: '#A6CAF0',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#40C1D6',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#40C1D6',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/idd/idd-background.jpg")',
  favicon: '/static/whitelabels/idd/idd-favicon.png',
  liveSubDomain: 'idd',
  sandboxSubDomain: 'idd',
};

const APW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'apw',
  readable: 'Anglo Pacific Wealth Management',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'apw',
  sandboxSubDomain: 'apw',
};

const ELG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'elg',
  readable: 'Elgin Group',
  logo: '/static/whitelabels/elg/elg-logo.jpg',
  logoType: '/static/whitelabels/elg/elg-logo.jpg',
  colourPrimary: '#84aeba',
  colourSecondary: '#badae3',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#84aeba',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#84aeba',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '',
  favicon: '/static/whitelabels/elg/elg-favicon.png',
  liveSubDomain: 'elg',
  sandboxSubDomain: 'elg',
};

const EXP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'exp',
  readable: 'Expatrimonia',
  logo: '/static/whitelabels/exp/exp-logo.jpg',
  logoType: '/static/whitelabels/exp/exp-logo.jpg',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/exp/exp-background.jpg")',
  favicon: '/static/whitelabels/exp/exp-favicon.png',
  liveSubDomain: 'exp',
  sandboxSubDomain: 'exp',
};

const HZN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hzn',
  readable: 'Horizon',
  logo: '/static/whitelabels/hzn/hzn-logo.png',
  logoType: '/static/whitelabels/hzn/hzn-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/hzn/hzn-background.jpg")',
  favicon: '/static/whitelabels/hzn/hzn-favicon.png',
  liveSubDomain: 'hzn',
  sandboxSubDomain: 'hzn',
};

const EGE: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ege',
  readable: 'Eagle Equity',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'ege',
  sandboxSubDomain: 'ege',
};

const IMP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'imp',
  readable: 'Imperius',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'imp',
  sandboxSubDomain: 'imp',
};

const HBC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hbc',
  readable: 'Heritage Banking Company',
  logo: '/static/whitelabels/hbc/hbc-logo.png',
  logoType: '/static/whitelabels/hbc/hbc-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/hbc/hbc-background.png")',
  favicon: '/static/whitelabels/hbc/hbc-favicon.png',
  liveSubDomain: 'hbc',
  sandboxSubDomain: 'hbc',
};

const CPT: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cpt',
  readable: 'PI LTD',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'cpt',
  sandboxSubDomain: 'cpt',
};

const GCF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gcf',
  readable: 'Global Choice Financial Group',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'gcf',
  sandboxSubDomain: 'gcf',
};

const BFT: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bft',
  readable: 'Beaufort Trade',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'bft',
  sandboxSubDomain: 'bft',
};

const AVB: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'avb',
  readable: 'Adviser Base',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'avb',
  sandboxSubDomain: 'avb',
};

const GMS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gms',
  readable: 'GMS',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'gms',
  sandboxSubDomain: 'gms',
};

const PWS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pws',
  readable: 'Prestige Wealth Solutions',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'pws',
  sandboxSubDomain: 'pws',
};

const OFP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ofp',
  readable: 'Offshore Planning',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'ofp',
  sandboxSubDomain: 'ofp',
};

const TDO: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'tdo',
  readable: 'Tradesto',
  logo: '/static/whitelabels/tdo/tdo-logo.png',
  logoType: '/static/whitelabels/tdo/tdo-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/tdo/tdo-background.jpg")',
  favicon: '',
  liveSubDomain: 'tdo',
  sandboxSubDomain: 'tdo',
};

const SDO: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sdo',
  readable: 'Scottsdale Overseas',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'sdo',
  sandboxSubDomain: 'sdo',
};

const TYT: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'tyt',
  readable: 'Tyton Capital',
  logo: '/static/whitelabels/tyt/tyt-logo.png',
  logoType: '/static/whitelabels/tyt/tyt-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/tyt/tyt-background.png")',
  favicon: '/static/whitelabels/tyt/tyt-favicon.png',
  liveSubDomain: 'tyt',
  sandboxSubDomain: 'tyt',
};

const TRH: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'trh',
  readable: 'Trinity Holdings',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'trh',
  sandboxSubDomain: 'trh',
};

const REG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'reg',
  readable: 'Regency International Group',
  logo: '/static/whitelabels/reg/reg-logo.png',
  logoType: '/static/whitelabels/reg/reg-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/reg/reg-background.jpg")',
  favicon: '/static/whitelabels/reg/reg-favicon.png',
  liveSubDomain: 'reg',
  sandboxSubDomain: 'reg',
};

const DGA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'dga',
  readable: 'De Global Access',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'dga',
  sandboxSubDomain: 'dga',
};

const QWG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'qwg',
  readable: 'Quantum Wealth Group',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'qwg',
  sandboxSubDomain: 'qwg',
};

const LWM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'lwm',
  readable: 'Lugano Wealth Management',
  logo: '/static/whitelabels/lwm/lwn-logo.png',
  logoType: '/static/whitelabels/lwm/lwn-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/lwm/lwn-background.jpg")',
  favicon: '/static/whitelabels/lwm/lwn-favicon.png',
  liveSubDomain: 'lwm',
  sandboxSubDomain: 'lwm',
};

const MGW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mgw',
  readable: 'Mayfair Global Wealth',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'mgw',
  sandboxSubDomain: 'mgw',
};

const CAS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cas',
  readable: 'Cambridge Associates',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'cas',
  sandboxSubDomain: 'cas',
};

const MPF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mpf',
  readable: 'MyPortfolio Direct',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'mpf',
  sandboxSubDomain: 'mpf',
};

const RCI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'rci',
  readable: 'Royal Crown Investments',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'rci',
  sandboxSubDomain: 'rci',
};

const GPS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gps',
  readable: 'Global Portfolio Strategies',
  logo: '/static/whitelabels/gps/gps-logo.jpg',
  logoType: '/static/whitelabels/gps/gps-logo.jpg',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/gps/gps-background.jpg")',
  favicon: '/static/whitelabels/gps/gps-favicon.png',
  liveSubDomain: 'gps',
  sandboxSubDomain: 'gps',
};

const AWM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'awm',
  readable: 'Ascott Wealth Management',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'awm',
  sandboxSubDomain: 'awm',
};

const IIL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'iil',
  readable: 'Intelligent Investments Ltd',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'iil',
  sandboxSubDomain: 'iil',
};

const IFP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ifp',
  readable: 'International Financial Partners',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'ifp',
  sandboxSubDomain: 'ifp',
};

const OWL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'owl',
  readable: 'Oscar Winson',
  logo: '/static/whitelabels/owl/owl-logo.png',
  logoType: '/static/whitelabels/owl/owl-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '/static/whitelabels/owl/owl-background.png',
  favicon: '',
  liveSubDomain: 'owl',
  sandboxSubDomain: 'owl',
};

const CSA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'csa',
  readable: 'Carrick South Africa',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'csa',
  sandboxSubDomain: 'csa',
};

const ESA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'esa',
  readable: 'Edge Financial',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'esa',
  sandboxSubDomain: 'esa',
};

const GML: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gml',
  readable: 'Gladstone Morgan Limited',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'gml',
  sandboxSubDomain: 'gml',
};

const ARL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'arl',
  readable: 'Arlo Associates',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'arl',
  sandboxSubDomain: 'arl',
};

const GCP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gcp',
  readable: 'Graeme Callaghan Pension Services',
  logo: '/static/whitelabels/gcp/gcp-logo.jpg',
  logoType: '/static/whitelabels/gcp/gcp-logo.jpg',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/gcp/gcp-background.jpg")',
  favicon: '/static/whitelabels/gcp/gcp-icon.jpg',
  liveSubDomain: 'gcp',
  sandboxSubDomain: 'gcp',
};

const NBA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'nba',
  readable: 'NEBA Financial Solutions',
  logo: '/static/whitelabels/nba/nba-logo.png',
  logoType: '/static/whitelabels/nba/nba-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/nba/nba-background.png")',
  favicon: '/static/whitelabels/nba/nba-icon.png',
  liveSubDomain: 'nba',
  sandboxSubDomain: 'nba',
};

const IWS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'iws',
  readable: 'Innovative Wealth Solution',
  logo: '/static/whitelabels/iws/iws-logo.png',
  logoType: '/static/whitelabels/iws/iws-logo.png',
  colourPrimary: '#1E324A',
  colourSecondary: '#E1EDED',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#1E324A',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1E324A',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/iws/iws-background.jpg")',
  favicon: '/static/whitelabels/iws/iws-favico.ico',
  liveSubDomain: 'iws',
  sandboxSubDomain: 'iws',
};

const BPF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bpf',
  readable: 'Blue Planet Financial',
  logo: '/static/whitelabels/bpf/bpf-logo.jpg',
  logoType: '/static/whitelabels/bpf/bpf-logo.jpg',
  colourPrimary: '#1E324A',
  colourSecondary: '#E1EDED',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#1E324A',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1E324A',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '',
  favicon: '/static/whitelabels/bpf/bpf-favicon.png',
  liveSubDomain: 'bpf',
  sandboxSubDomain: 'bpf',
};

const TBA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'tba',
  readable: 'Temple Bar Asia',
  logo: '/static/whitelabels/tba/tba-logo.png',
  logoType: '/static/whitelabels/tba/tba-logo.png',
  colourPrimary: '#1E324A',
  colourSecondary: '#E1EDED',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#1E324A',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1E324A',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/tba/tba-background.jpg")',
  favicon: '/static/whitelabels/tba/tba-favicon.png',
  liveSubDomain: 'tba',
  sandboxSubDomain: 'tba',
};

const PPF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ppf',
  readable: 'Prism Partners Financial',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'ppf',
  sandboxSubDomain: 'ppf',
};

const MGA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mga',
  readable: 'McKinley Global Advisors',
  logo: '/static/whitelabels/mga/mga-logo.png',
  logoType: '/static/whitelabels/mga/mga-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/mga/mga-background.png")',
  favicon: '/static/whitelabels/mga/mga-favicon.png',
  liveSubDomain: 'mga',
  sandboxSubDomain: 'mga',
};

const ACS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'acs',
  readable: 'Albion Capital Strategies',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'acs',
  sandboxSubDomain: 'acs',
};

const ADW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'adw',
  readable: 'Advies Wealth LTD',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'adw',
  sandboxSubDomain: 'adw',
};

const LLE: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'lle',
  readable: 'Legend Leader Enterprises',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'lle',
  sandboxSubDomain: 'lle',
};

const IIM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'iim',
  readable: 'IIMG Africa',
  logo: '/static/whitelabels/iim/iim-logo.png',
  logoType: '/static/whitelabels/iim/iim-logo.png',
  colourPrimary: '#1E324A',
  colourSecondary: '#E1EDED',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#1E324A',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#1E324A',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '/static/whitelabels/iim/iim-background.jpg',
  favicon: '/static/whitelabels/iim/iim-favicon.jpg',
  liveSubDomain: 'iim',
  sandboxSubDomain: 'iim',
};

const PGC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'pgc',
  readable: 'Premier Global Consulting',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'pgc',
  sandboxSubDomain: 'pgc',
};

const GCI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gci',
  readable: 'Jiangsu Zhan Xing Investment Company',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'gci',
  sandboxSubDomain: 'gci',
};

const FOR: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'for',
  readable: 'Forth Capital',
  logo: '/static/whitelabels/for/for-logo.png',
  logoType: '/static/whitelabels/for/for-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/for/for-background.png")',
  favicon: '/static/whitelabels/for/for-favicon.png',
  liveSubDomain: 'for',
  sandboxSubDomain: 'for',
};

const QIN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'qin',
  readable: 'QROPS India Ltd',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'qin',
  sandboxSubDomain: 'qin',
};

const CLW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'clw',
  readable: 'Claremont Wealth',
  logo: '/static/whitelabels/clw/clw-logo.png',
  logoType: '/static/whitelabels/clw/clw-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/clw/clw-background.jpg")',
  favicon: '/static/whitelabels/clw/clw-favicon.png',
  liveSubDomain: 'clw',
  sandboxSubDomain: 'clw',
};

const GSW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gsw',
  readable: 'Guardstone Wealth Management',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'gsw',
  sandboxSubDomain: 'gsw',
};

const ATM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'atm',
  readable: 'Atom Alternatives',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'atm',
  sandboxSubDomain: 'atm',
};

const PRC: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'prc',
  readable: 'Pearl River',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'prc',
  sandboxSubDomain: 'prc',
};

const EFS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'efs',
  readable: 'EFS',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'efs',
  sandboxSubDomain: 'efs',
};

const SEL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sel',
  readable: 'Select Asset',
  logo: '/static/whitelabels/sel/sel-logo.png',
  logoType: '/static/whitelabels/sel/sel-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/sel/sel-background.jpg")',
  favicon: '/static/whitelabels/sel/sel-favicon.png',
  liveSubDomain: 'sel',
  sandboxSubDomain: 'sel',
};

const TRI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'tri',
  readable: 'Trireme',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'tri',
  sandboxSubDomain: 'tri',
};

const HAW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'haw',
  readable: 'Hawthorn ',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'haw',
  sandboxSubDomain: 'haw',
};

const BET: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'bet',
  readable: 'Betfred',
  logo: '/static/whitelabels/bet/bet-logo.png',
  logoType: '/static/whitelabels/bet/bet-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '',
  favicon: '/static/whitelabels/bet/bet-favicon.jpg',
  liveSubDomain: 'bet',
  sandboxSubDomain: 'bet',
};

const SRK: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'srk',
  readable: 'SuisseRock',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'srk',
  sandboxSubDomain: 'srk',
};

const ASE: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ase',
  readable: 'Asea Capital',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'ase',
  sandboxSubDomain: 'ase',
};

const QCS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'qcs',
  readable: 'QROPS Consultancy Services',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'qcs',
  sandboxSubDomain: 'qcs',
};

const ACH: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ach',
  readable: 'Achieve Capital',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'ach',
  sandboxSubDomain: 'ach',
};

const IGM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'igm',
  readable: 'Invest Global Management',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'igm',
  sandboxSubDomain: 'igm',
};

const CDL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cdl',
  readable: 'Cardinal',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'cdl',
  sandboxSubDomain: 'cdl',
};

const UEP: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'uep',
  readable: 'UEP',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'uep',
  sandboxSubDomain: 'uep',
};

const PRG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'prg',
  readable: 'Prism Group International',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'prg',
  sandboxSubDomain: 'prg',
};

const MMG: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mmg',
  readable: 'MMG Associates',
  logo: '/static/whitelabels/mmg/mmg-logo.png',
  logoType: '/static/whitelabels/mmg/mmg-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/mmg/mmg-background.png")',
  favicon: '/static/whitelabels/mmg/mmg-favicon.png',
  liveSubDomain: 'mmg',
  sandboxSubDomain: 'mmg',
};

const EDM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'edm',
  readable: 'Edge Wealth Management Mauritius',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'edm',
  sandboxSubDomain: 'edm',
};

const UWM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'uwm',
  readable: 'Upstream Wealth Management',
  logo: '/static/whitelabels/uwm/uwm-logo.png',
  logoType: '/static/whitelabels/uwm/uwm-logo.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/uwm/uwm-background.jpg"',
  favicon: '/static/whitelabels/uwm/uwm-favicon.png',
  liveSubDomain: 'uwm',
  sandboxSubDomain: 'uwm',
};

const AFL: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'afl',
  readable: 'Atwal Financial Limited',
  logo: '/static/whitelabels/afl/afl-logo.png',
  logoType: '/static/whitelabels/afl/afl-logo.png',
  colourPrimary: '#013366',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#013366',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#013366',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/afl/afl-background.png")',
  favicon: '/static/whitelabels/afl/afl-favicon.png',
  liveSubDomain: 'afl',
  sandboxSubDomain: 'afl',
};

const GJH: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gjh',
  readable: 'Greg Hirst',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'gjh',
  sandboxSubDomain: 'gjh',
};

const JIN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'jin',
  readable: 'J Investments',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'jin',
  sandboxSubDomain: 'jin',
};

const CIM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cim',
  readable: 'Crystal Investment Management',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'cim',
  sandboxSubDomain: 'cim',
};

const IVW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ivw',
  readable: 'Ivory Wealth Management',
  logo: '/static/whitelabels/ivw/ivw-banner.png',
  logoType: '/static/whitelabels/ivw/ivw-banner.png',
  colourPrimary: '#610000',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#610000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#610000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/ivw/ivw-background.jpeg',
  favicon: '/static/whitelabels/ivw/ivw-icon.png',
  liveSubDomain: 'ivw',
  sandboxSubDomain: 'ivw',
};

const FSA: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fsa',
  readable: 'Financial Solutions Asia',
  logo: '/static/whitelabels/fsa/fsa-bann.png',
  logoType: '/static/whitelabels/fsa/fsa-logo.png',
  colourPrimary: '#013366',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#013366',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#013366',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/fsa/fsa-background.jpg")',
  favicon: '/static/whitelabels/fsa/fsa-favicon.jpeg',
  liveSubDomain: 'fsa',
  sandboxSubDomain: 'fsa',
};

const MCW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mcw',
  readable: 'MCW Administration Ltd',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'mcw',
  sandboxSubDomain: 'mcw',
};

const EAS: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'eas',
  readable: 'Edge Advisory Services',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'eas',
  sandboxSubDomain: 'eas',
};

const PPI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'ppi',
  readable: 'Professional Portfolio International Ltd',
  logo: '/static/whitelabels/ppi/ppi-logo.png',
  logoType: '/static/whitelabels/ppi/ppi-logo.png',
  colourPrimary: '#2076BD',
  colourSecondary: '#EDE4E4',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#2076BD',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#2076BD',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: '',
  favicon: '/static/whitelabels/ppi/ppi-favicon.png',
  liveSubDomain: 'ppi',
  sandboxSubDomain: 'ppi',
};

const ISV: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'isv',
  readable: 'Intersav ( Thailand ) CO. LTD',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'isv',
  sandboxSubDomain: 'isv',
};

const SMI: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'smi',
  readable: 'Smile FS Ltd',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'smi',
  sandboxSubDomain: 'smi',
};

const SEN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'sen',
  readable: 'Sentino Superyacht Consultants',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'sen',
  sandboxSubDomain: 'sen',
};

const CPD: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'cpd',
  readable: ' atforms Direct',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'cpd',
  sandboxSubDomain: 'cpd',
};

const FCM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fcm',
  readable: 'Finesco Capital Management',
  logo: '/static/cp-logo.png',
  logoType: '/static/cp-logo.png',
  colourPrimary: '#003479',
  colourSecondary: '#003479',
  colourLight: '#003479dd',
  colourLighter: '#00347915',
  buttonColour: '#003479',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#003479',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/cp-wave-bg.png")',
  favicon: '/static/favicon.ico',
  liveSubDomain: 'fcm',
  sandboxSubDomain: 'fcm',
};

const FIW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fiw',
  readable: 'Finsbury Wealth',
  logo: '/static/whitelabels/fiw/finsbury-wealth.png',
  logoType: '/static/whitelabels/fiw/finsbury-wealth.png',
  colourPrimary: '#12345B',
  colourSecondary: '#A59664',
  colourLight: '#A59664',
  colourLighter: '#B21E4415',
  buttonColour: '#12345B',
  buttonBgColour: '#EFEFEF',
  colourTitle: '#12345B',
  bgColourPrimary: '#EAECEE',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: 'Libre Caslon Text',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Libre+Caslon+Text:ital,wght@0,400;0,700;1,400&family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/fiw/finsbury-wealth-login.png")',
  favicon: '/static/whitelabels/fiw/finsbury-wealth-fav.png',
  liveSubDomain: 'fiw',
  sandboxSubDomain: 'fiw',
};

const MCM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'mcm',
  readable: 'Moran Capital Management',
  logo: '/static/whitelabels/mcm/mcm-logo.png',
  logoType: '/static/whitelabels/mcm/mcm-logo.png',
  colourPrimary: '#0D0F10',
  colourSecondary: '#02568a',
  colourLight: '#02568add',
  colourLighter: '#02568a15',
  buttonColour: '#02568a',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#0D0F10',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Nunito Sans',
  fontSecondary: 'Nunito Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;600&family=Poppins:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/mcm/mcm-background.jpg")',
  favicon: '/static/whitelabels/mcm/mcm-favicon.png',
  liveSubDomain: 'mcm',
  sandboxSubDomain: 'mcm',
};

const HCF: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hcf',
  readable: 'H Capital',
  logo: '/static/whitelabels/hcf/logo.png',
  logoType: '/static/whitelabels/hcf/logo.png',
  colourPrimary: '#527c7e',
  colourSecondary: '#033839',
  colourLight: '#527c7e',
  colourLighter: '#033839',
  buttonColour: '#527c7e',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#527c7e',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#033839',
  favicon: '/static/whitelabels/hcf/favicon.png',
  liveSubDomain: 'hcf',
  sandboxSubDomain: 'hcf',
};

const FYN: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fyn',
  readable: 'FYNIX Wealth',
  logo: '/static/whitelabels/fyn/logo.png',
  logoType: '/static/whitelabels/fyn/logo.png',
  colourPrimary: '#102c2f',
  colourSecondary: '#59c58b',
  colourLight: '#59c58b',
  colourLighter: '#033839',
  buttonColour: '#102c2f',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#102c2f',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/fyn/bg.png")',
  favicon: '/static/whitelabels/fyn/favicon.png',
  liveSubDomain: 'fyn',
  sandboxSubDomain: 'fyn',
};

const HPH: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hph',
  readable: 'HUGHES PRIVATE HOLDINGS LIMITED',
  logo: '/static/whitelabels/hph/logo-new.png',
  logoType: '/static/whitelabels/hph/logo-new.png',
  colourPrimary: '#000000',
  colourSecondary: '#b3893d',
  colourLight: '#b3893d',
  colourLighter: '#b3893d',
  buttonColour: '#000000',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#000000',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: '#000000',
  favicon: '/static/whitelabels/hph/favicon.png',
  liveSubDomain: 'hph',
  sandboxSubDomain: 'hph',
};

const FOW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'fow',
  readable: 'Forest Wealth SA',
  logo: '/static/whitelabels/fow/fow-logo.png',
  logoType: '/static/whitelabels/fow/fow-logo.png',
  colourPrimary: '#0B3D3C',
  colourSecondary: '#B5C9AE',
  colourLight: '',
  colourLighter: '',
  buttonColour: '#0B3D3C',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#0B3D3C',
  bgColourPrimary: '#FCFFFB',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Roboto',
  fontSecondary: 'Roboto',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/fow/fow-background.png")',
  favicon: '/static/whitelabels/fow/fow-favicon.png',
  liveSubDomain: 'fow',
  sandboxSubDomain: 'fow',
};

const KIM: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'kim',
  readable: 'KMI Mauritius Ltd',
  logo: '/static/whitelabels/kmi/kmi-logo.gif',
  logoType: '/static/whitelabels/kmi/kmi-logo.gif',
  colourPrimary: '#183562',
  colourSecondary: '#ae9666',
  colourLight: '#ae9666dd',
  colourLighter: '#ae966615',
  buttonColour: '#183562',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#183562',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: '',
  fontSecondary: '',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/kmi/kmi-background.jpg")',
  favicon: '/static/whitelabels/kmi/kmi-favicon.png',
  liveSubDomain: 'kim',
  sandboxSubDomain: 'kim',
};

const HCY: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'hcy',
  readable: 'Holborn Assets Wealth Management (CY) LTD',
  logo: '/static/whitelabels/hcy/hcy-logo.png',
  logoType: '/static/whitelabels/hcy/hcy-logo.png',
  colourPrimary: '#023660',
  colourSecondary: '#007fb8',
  colourLight: '#007fb8dd',
  colourLighter: '#007fb815',
  buttonColour: '#023660',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#023660',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Open Sans',
  fontSecondary: 'Open Sans',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/hcy/hcy-background.jpg")',
  favicon: '/static/whitelabels/hcy/hcy-favicon.png',
  liveSubDomain: 'hcy',
  sandboxSubDomain: 'hcy',
};

const ERW: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'erw',
  readable: 'Erwin',
  logo: '/static/whitelabels/imc/imc-logo.png',
  logoType: '/static/whitelabels/imc/imc-logo.png',
  colourPrimary: '#903600',
  colourSecondary: '#903600',
  colourLight: '#7A7A7A',
  colourLighter: '#4C6BAC15',
  buttonColour: '#141412',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#141412',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Inter',
  fontSecondary: 'Inter',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/imc/imc-background.jpg")',
  favicon: '/static/whitelabels/imc/imc-favicon.png',
  liveSubDomain: 'erw',
  sandboxSubDomain: 'erw',
};

const PER: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'per',
  readable: 'Prospera Management',
  logo: '/static/whitelabels/per/per-logo.png',
  logoType: '/static/whitelabels/per/per-logo.png',
  colourPrimary: '#212C3D',
  colourSecondary: '#C6B688',
  colourLight: '#7A7A7A',
  colourLighter: '#4C6BAC15',
  buttonColour: '#141412',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#141412',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Inter',
  fontSecondary: 'Inter',
  fontURL:
    'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600&display=swap',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/per/per-background.png")',
  favicon: '/static/whitelabels/imc/imc-favicon.png',
  liveSubDomain: 'per',
  sandboxSubDomain: 'per',
};

const GAV: Whitelabel = {
  name() {
    return whiteName.call(this);
  },
  code: 'gav',
  readable: 'Gavin Broad',
  logo: '/static/whitelabels/gav/gav-logo.png',
  logoType: '/static/whitelabels/gav/gav-logo.png',
  colourPrimary: '#007075',
  colourSecondary: '#007075',
  colourLight: '#0068B7dd',
  colourLighter: '#0068B715',
  buttonColour: '#007075',
  buttonBgColour: '#FFFFFF',
  colourTitle: '#007075',
  bgColourPrimary: '#EFEFEF',
  bgColourSecondary: '#FFFFFF',
  fontPrimary: 'Mignon',
  fontSecondary: 'Mignon',
  fontURL: '',
  heroBg: '',
  loginBg: 'url("/static/whitelabels/gav/gav-background.jpg")',
  favicon: '/static/whitelabels/gav/gav-favicon.png',
  liveSubDomain: 'gav',
  sandboxSubDomain: 'gav',
};

const whitelabels: Whitelabel[] = [
  FLB,
  CP,
  SAY,
  SVN,
  SVA,
  AMA,
  HEX,
  HUK,
  SFP,
  JIB,
  BGA,
  FIN,
  AMI,
  FOA,
  CRK,
  GLO,
  NEB,
  MML,
  ABF,
  CBA,
  HAL,
  KMI,
  CHB,
  PTM,
  FAW,
  FSP,
  LSM,
  NWA,
  BLU,
  CHC,
  EDG,
  FUT,
  GND,
  RWM,
  RWB,
  RAP,
  OPC,
  MNP,
  IMC,
  TSF,
  EDW,
  CHL,
  ACO,
  AFG,
  BLA,
  BLC,
  BUK,
  BWM,
  CAP,
  CCM,
  CGI,
  CHA,
  CIC,
  CPV,
  CRE,
  CSJ,
  EPA,
  EQI,
  EWL,
  FFC,
  FIT,
  FMA,
  FRD,
  FSN,
  GAG,
  KBP,
  OIB,
  OYS,
  SEA,
  TFL,
  TWM,
  WIN,
  GIG,
  GUI,
  GWM,
  HBS,
  HCM,
  ICH,
  IMG,
  KPI,
  LAA,
  LAT,
  LSS,
  LWI,
  MQR,
  NFS,
  OPF,
  PAM,
  PFA,
  PRE,
  QAS,
  REI,
  SAL,
  SAM,
  SIM,
  SIP,
  THG,
  USU,
  STC,
  TRU,
  BCI,
  CPS,
  IPP,
  NEBO,
  NEW,
  SBC,
  WMG,
  GWS,
  LEG,
  MPV,
  LAW,
  ORE,
  TMG,
  AIS,
  ADF,
  GIS,
  AGT,
  HWW,
  FAR,
  RGI,
  TZN,
  CHU,
  BAB,
  CIS,
  GRA,
  PUM,
  CWZ,
  SEV,
  ICA,
  AIB,
  JSG,
  SYN,
  IND,
  BLR,
  MCG,
  AME,
  PCC,
  AST,
  GPO,
  AGM,
  APC,
  HEC,
  BGT,
  PDG,
  PDO,
  AML,
  CED,
  ABD,
  AIF,
  IDO,
  FFP,
  BBG,
  OFS,
  VIO,
  CAM,
  EAB,
  AMB,
  CSM,
  JJI,
  IOD,
  BIG,
  CAR,
  CAC,
  SIJ,
  UNI,
  RHI,
  PAR,
  PHX,
  CSL,
  PFM,
  BDW,
  OPL,
  PIC,
  BAM,
  TTG,
  MIP,
  CWF,
  MR,
  CAT,
  LIF,
  AAL,
  LHS,
  MFD,
  IDD,
  APW,
  ELG,
  EXP,
  HZN,
  EGE,
  IMP,
  HBC,
  FIW,
  CPT,
  GCF,
  BFT,
  AVB,
  GMS,
  PWS,
  OFP,
  TDO,
  SDO,
  TYT,
  TRH,
  REG,
  DGA,
  QWG,
  LWM,
  MGW,
  CAS,
  MPF,
  RCI,
  GPS,
  AWM,
  IIL,
  IFP,
  OWL,
  CSA,
  ESA,
  GML,
  ARL,
  GCP,
  NBA,
  IWS,
  BPF,
  TBA,
  PPF,
  MGA,
  ACS,
  ADW,
  LLE,
  IIM,
  PGC,
  GCI,
  FOR,
  QIN,
  CLW,
  GSW,
  ATM,
  PRC,
  EFS,
  SEL,
  TRI,
  HAW,
  BET,
  SRK,
  ASE,
  QCS,
  ACH,
  IGM,
  CDL,
  UEP,
  PRG,
  MMG,
  EDM,
  UWM,
  AFL,
  GJH,
  JIN,
  CIM,
  IVW,
  FSA,
  MCW,
  EAS,
  PPI,
  ISV,
  SMI,
  SEN,
  CPD,
  FCM,
  MCM,
  HCF,
  FYN,
  HPH,
  FOW,
  KIM,
  HCY,
  ERW,
  PER,
  GAV
];

export { whitelabels };
